import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Spinner,
  Badge,
  useToast,
  useClipboard,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useBreakpointValue,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from '@chakra-ui/react';
import { Users, Bot, Copy, Check, LogOut, Volume2, VolumeX, RefreshCw, Trophy, Target, Coins } from 'lucide-react';
import gameApiService from '../services/gameApiService';
import { useAuth } from '../contexts/AuthContext';
import { useSkillLevel } from '../contexts/SkillLevelContext';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import PokerTableSVG from '../components/PokerTableSVG';
import gameEngine from '../services/gameEngine';
import handEvaluationService from '../services/handEvaluationService';
import {
  subscribeToTrainingTable,
  startTrainingGame,
  leaveTrainingTable,
  updateTrainingGameState,
  updateTrainingTableBots,
  sendTrainingInvite,
  recordChallengeProgress,
  createBots,
  makeBotDecision,
  getBotThinkingDelay,
  trainingDecisionLogger,
  type TrainingTable,
} from '../services/training';
import { GameState, PlayerAction } from '../types/GameState';
import { getFriends } from '../services/socialService';
import geoService from '../services/geoService';
import { POKER_GLOSSARY } from '../services/training/trainingAnalysisService';
import { CONCEPT_GLOSSARY_KEYS } from '../services/training/tournamentChallenges';
import { pssBustForBot } from '../utils/pssBots';
import { setGtoContext, clearGtoContext, positionFromSeat, vsSpotFor } from '../services/training/gtoContext';
import { handCodeFromCards } from '../data/gtoRanges';
import { playTableSfx, playHandResultSfx, stopAllTableSfx } from '../utils/tableSfx';

// ============================================================================
// MOBILE UTILITIES
// ============================================================================

const triggerHaptic = (type: 'light' | 'medium' | 'heavy' | 'success' = 'light') => {
  if ('vibrate' in navigator) {
    const patterns = { light: [10], medium: [25], heavy: [50], success: [10, 50, 20] };
    navigator.vibrate(patterns[type]);
  }
};

const TOUCH_TARGETS = {
  minimum: '44px',
  comfortable: '48px',
  large: '56px',
};

// ============================================================================
// SOUND SYSTEM - Uses your MP3 files in public/sounds/
// Maps simplified action names to actual filenames
// ============================================================================
// All table sounds go through utils/tableSfx: one short card drop per
// street, the deal shuffle only at hand start and trimmed to the deal, the
// win jingle only for the player who won and never on top of itself.
const playSound = (soundName: string, enabled: boolean) => {
  if (!enabled) return;
  try {
    playTableSfx(soundName, true);
  } catch (e) {
    // Ignore sound errors
  }
};

// Hard-stop every sound this page started - leaving a table must not leave
// a victory jingle playing over the lobby.
const stopAllSounds = () => stopAllTableSfx();

// ============================================================================
// EQUITY HELPER - Monte Carlo win percentages for the all-in runout
// ============================================================================
// Same suspense as the live tables: each live player's win % rendered on
// their seat, recomputed as every street lands. ~160 simulated runouts per
// street is plenty for a display percentage and runs in a few ms.
const computeTrainingEquities = (state: GameState): { [id: string]: number } => {
  try {
    const live = state.players.filter(
      p => !p.folded && !p.eliminated && p.holeCards && p.holeCards.length === 2
    );
    if (live.length < 2) return {};
    const board = state.communityCards || [];
    const deck = (state.deck || []).slice();
    const need = 5 - board.length;
    const ITER = need === 0 ? 1 : 160;
    const wins: Record<string, number> = {};
    live.forEach(p => { wins[p.id] = 0; });
    for (let i = 0; i < ITER; i++) {
      const d = deck.slice();
      for (let k = d.length - 1; k > 0; k--) {
        const j = Math.floor(Math.random() * (k + 1));
        [d[k], d[j]] = [d[j], d[k]];
      }
      const fullBoard = [...board, ...d.slice(0, need)];
      let best = -1;
      let winners: string[] = [];
      for (const p of live) {
        const hand = handEvaluationService.evaluateHand(p.holeCards!, fullBoard);
        const v = hand ? handEvaluationService.getHandValue(hand) : 0;
        if (v > best) { best = v; winners = [p.id]; }
        else if (v === best) winners.push(p.id);
      }
      winners.forEach(w => { wins[w] += 1 / winners.length; });
    }
    const out: Record<string, number> = {};
    live.forEach(p => { out[p.id] = Math.round((wins[p.id] / ITER) * 100); });
    return out;
  } catch (err) {
    console.warn('[EQUITY] calc failed:', err);
    return {};
  }
};

// Equity as of a partial board (used while the runout animation replays the
// streets): the not-yet-revealed board cards go back into the simulation
// pool so the odds match what a live viewer would compute at that street.
const computeEquitiesAtStreet = (state: GameState, visibleCount: number): { [id: string]: number } => {
  const board = state.communityCards || [];
  return computeTrainingEquities({
    ...state,
    communityCards: board.slice(0, visibleCount),
    deck: [...(state.deck || []), ...board.slice(visibleCount)],
  } as GameState);
};

// ============================================================================
// SHOWDOWN HELPER - Evaluates hands and determines winners
// ============================================================================
const evaluateShowdown = (gameState: GameState) => {
  try {
    const activePlayers = gameState.players.filter(p => !p.folded && !p.eliminated);
    
    if (activePlayers.length === 0) return null;
    
    // Evaluate each player's hand
    const playerHands = activePlayers.map(player => {
      const hand = handEvaluationService.evaluateHand(
        player.holeCards || [],
        gameState.communityCards || []
      );
      return {
        playerId: player.id,
        hand,
        handValue: hand ? handEvaluationService.getHandValue(hand) : 0,
        handDescription: hand ? handEvaluationService.getHandDescription(hand) : 'Unknown',
      };
    });

    // Sort by hand value (highest first)
    playerHands.sort((a, b) => b.handValue - a.handValue);

    // Winner is the one with highest hand value
    const winner = playerHands[0];
    
    return {
      winners: [{
        playerId: winner.playerId,
        amount: gameState.pot,
        hand: winner.handDescription,
      }],
    };
  } catch (err) {
    console.error('Showdown evaluation error:', err);
    return null;
  }
};

// ============================================================================
// CONSTANTS FOR FREEZE PROTECTION
// ============================================================================
const WATCHDOG_INTERVAL = 5000; // Check every 5 seconds
const BOT_TURN_TIMEOUT = 12000; // Force action if bot takes > 12 seconds
const STUCK_DETECTION_TIME = 15000; // Consider stuck if no activity for 15 seconds

const TrainingGamePage: React.FC = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { currentUser, userProfile } = useAuth();
  const { recordHandPlayed } = useSkillLevel();

  //  Sound state

  const [soundEnabled, setSoundEnabled] = useState(true);

  // State
  const [table, setTable] = useState<TrainingTable | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showRaiseSlider, setShowRaiseSlider] = useState(false);
  const [raiseAmount, setRaiseAmount] = useState(0);
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // 🆕 FREEZE PROTECTION STATE
  const [isStuck, setIsStuck] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());

  // 🏆 CHALLENGE MINI-GAME STATE (tables created from Tournament Training)
  const [challengeResult, setChallengeResult] = useState<{
    outcome: 'won' | 'lost';
    heroChips: number;
    handsPlayed: number;
  } | null>(null);
  const [challengeClaiming, setChallengeClaiming] = useState(false);
  const [challengeClaimMsg, setChallengeClaimMsg] = useState<string | null>(null);
  // Mission briefing: shown before the first decision so the player knows
  // exactly what they're trying to do and how to do it.
  const [briefingDismissed, setBriefingDismissed] = useState(false);
  // 📲 Friend invites from the briefing - list, sent-tracker, loading
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [friendsList, setFriendsList] = useState<any[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  // Steal-drill tracking: pots won before the flop (goalType 'preflopWins').
  const [preflopWins, setPreflopWins] = useState(0);
  const preflopWinsRef = useRef(0);
  // Per-client settle guards: each player evaluates THEIR OWN challenge
  // outcome from the table subscription (co-op safe - the hand may have
  // been closed by any client). settledRef prevents re-firing; the hand
  // ref prevents double-counting a hand end delivered twice.
  const challengeSettledRef = useRef(false);
  const lastEvaluatedHandRef = useRef(0);

  // Hydrate persisted counter progress (steal drill) after refresh/rejoin.
  const progressHydratedRef = useRef(false);

  // 🎬 ALL-IN RUNOUT STATE - drives PokerTableSVG's suspense machinery
  // (per-seat equity badges, winner gating) exactly like the live tables.
  const [trainingRunoutPhase, setTrainingRunoutPhase] = useState<'none' | 'running' | 'complete'>('none');
  const [trainingEquities, setTrainingEquities] = useState<{ [id: string]: number }>({});
  // Local animation cursor: how many board cards are visible right now, and
  // the pot to display while the state's pot is already zeroed by the award.
  const [runoutBoardCount, setRunoutBoardCount] = useState(0);
  const [runoutPotDisplay, setRunoutPotDisplay] = useState<number | null>(null);
  // One animation per hand per client, survives re-renders and echoes.
  const lastRunoutAnimatedRef = useRef(0);
  // Re-entry lock: each street write during the staged runout re-fires the
  // Firestore subscription, whose effects call checkAndProgressRound again
  // with an intermediate all-in state. Without this lock a SECOND runout
  // started concurrently and the two loops fought (interleaved writes,
  // next-hand timers clearing each other) - hands froze.
  const runoutStagingRef = useRef(false);

  // ⏱ TOURNAMENT-STYLE TURN TIMER - the real-table feel. 25s to act on your
  // turn, auto-fold (auto-check when free) at zero, ring rendered by the
  // same PokerTableSVG timer the live tables use.
  const TRAINING_TURN_SECONDS = 25;
  const [turnTimeLeft, setTurnTimeLeft] = useState(TRAINING_TURN_SECONDS);
  const turnTimerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ⏸ PAUSE - single-player privilege: freeze the hand timer and bot
  // actions while you think, grab a drink, or study the board. In co-op
  // only the host's client drives bots, so the host's pause halts play
  // for the table.
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  // 📈 BLIND ESCALATION (challenge tables) - blinds jump every 5 hands like
  // a turbo tournament. Level multipliers over the challenge's base blinds.
  const BLIND_LEVEL_HANDS = 5;
  const BLIND_MULTIPLIERS = [1, 1.5, 2, 3, 4, 6, 8];
  const getBlindsForHand = useCallback((handNum: number): { sb: number; bb: number; ante: number; level: number } => {
    const baseSb = table?.smallBlind || 1;
    const baseBb = table?.bigBlind || 2;
    if (!(table as any)?.challenge) return { sb: baseSb, bb: baseBb, ante: 0, level: 1 };
    const levelIdx = Math.min(
      BLIND_MULTIPLIERS.length - 1,
      Math.floor(Math.max(0, handNum - 1) / BLIND_LEVEL_HANDS)
    );
    const mult = BLIND_MULTIPLIERS[levelIdx];
    // Round to clean chip denominations
    const sb = Math.max(baseSb, Math.round((baseSb * mult) / 50) * 50 || baseSb);
    const bb = sb * 2;
    // Antes kick in at level 3, ~12.5% of the big blind (classic per-player
    // ante), rounded to clean chips - just like the real blind schedule.
    const level = levelIdx + 1;
    const ante = level >= 3 ? Math.max(25, Math.round(bb / 8 / 25) * 25) : 0;
    return { sb, bb, ante, level };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  // Refs
  const unsubscribeRef = useRef<(() => void) | null>(null);
  // Latest game state for timers that must not act on stale closures
  const gameStateRef = useRef<GameState | null>(null);
  // One forced re-deal per hand, max
  const lastForcedDealHandRef = useRef(0);
  // Double-tap confirmation for folding when checking is free
  const confirmFreeFoldRef = useRef(false);
  const botTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingBotRef = useRef(false);
  const nextHandTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRoundRef = useRef<string | null>(null);
  const watchdogTimerRef = useRef<NodeJS.Timeout | null>(null);
  const botTurnStartRef = useRef<number | null>(null);

  // Clipboard for invite code
  const { onCopy, hasCopied } = useClipboard(table?.inviteCode || '');

  const currentUserId = currentUser?.uid || '';
  const username = userProfile?.username || userProfile?.displayName || 'Player';
  const isHost = table?.hostId === currentUserId;

  // Hydrate persisted counter progress (steal drill) after refresh/rejoin.
  // Lives below the currentUserId declaration - the dependency array is
  // evaluated during render, so referencing it earlier throws a TDZ error.
  useEffect(() => {
    if (progressHydratedRef.current || !table || !currentUserId) return;
    const saved = (table as any)?.challengeProgress?.[currentUserId]?.preflopWins;
    if (typeof saved === 'number' && saved > preflopWinsRef.current) {
      preflopWinsRef.current = saved;
      setPreflopWins(saved);
      // Don't re-evaluate hands from before the refresh.
      lastEvaluatedHandRef.current = Math.max(lastEvaluatedHandRef.current, (gameState?.handNumber || 1) - 1);
      console.log(`🥷 [CHALLENGE] Hydrated preflop wins: ${saved}`);
    }
    progressHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, currentUserId]);

  // Build username map for PokerTable component
  const usernameMap: { [odwinId: string]: string } = {};
  if (table) {
    table.players.forEach((odwinId, idx) => {
      usernameMap[odwinId] = table.playerNames?.[idx] || `Player ${idx + 1}`;
    });
    table.bots.forEach(bot => {
      usernameMap[bot.uid] = bot.username;
    });
  }

  // Computed values for action buttons
  const currentPlayer = gameState?.players.find(p => p.id === currentUserId);

  // Publish the player's seat and stack so the floating Charts button opens
  // the preflop chart for this exact spot. Cleared when leaving the table.
  const myStackChips = currentPlayer?.chips ?? 0;
  const myPlayerCount = gameState?.players.filter(p => !p.eliminated).length ?? 0;
  const myDealerIndex = gameState?.dealerButton ?? 0;
  const myIndex = gameState ? gameState.players.findIndex(p => p.id === currentUserId) : -1;
  const myHandCode = currentPlayer && !currentPlayer.folded ? handCodeFromCards(currentPlayer.holeCards) : null;
  // The action in front of me this hand: who raised preflop (if anyone) and
  // whether the flop is out. Bots act between renders, so this is derived
  // fresh from the game state and drives the chart the drawer opens on.
  const myStage: 'preflop' | 'postflop' = gameState && gameState.round !== 'preflop' && gameState.round !== 'ended' ? 'postflop' : 'preflop';
  const bbForRaise = table?.bigBlind || 2;
  const preflopRaiserIndex = gameState && gameState.round === 'preflop'
    ? gameState.players.reduce((best, p, i) => {
        if (i === myIndex || !p || p.folded) return best;
        const bet = p.currentBet || 0;
        if (bet <= bbForRaise) return best;
        return best < 0 || bet > (gameState.players[best]?.currentBet || 0) ? i : best;
      }, -1)
    : -1;
  useEffect(() => {
    if (!gameState || myIndex < 0) return;
    const bb = table?.bigBlind || 2;
    const stackBB = myStackChips / bb;
    const position = positionFromSeat(gameState.players.length, myDealerIndex, myIndex);
    const facingRaise = preflopRaiserIndex >= 0 && myStage === 'preflop';
    const raiserPos = facingRaise ? positionFromSeat(gameState.players.length, myDealerIndex, preflopRaiserIndex) : '';
    const raiserName = facingRaise ? (usernameMap[gameState.players[preflopRaiserIndex]?.id] || raiserPos) : '';
    setGtoContext({
      position,
      stackBB,
      situation: facingRaise ? 'vs' : stackBB <= 20 ? 'push' : 'open',
      spot: facingRaise ? vsSpotFor(position, raiserPos) : undefined,
      stage: myStage,
      actionNote: facingRaise ? `${raiserName} (${raiserPos}) raised in front of you` : undefined,
      hand: myHandCode,
      label: `${position} · ${Math.round(stackBB)} BB${facingRaise ? ' · vs raise' : myStage === 'postflop' ? ' · postflop' : ''}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myIndex, myDealerIndex, myPlayerCount, Math.round(myStackChips / (table?.bigBlind || 2)), gameState?.handNumber, myHandCode, preflopRaiserIndex, myStage]);
  useEffect(() => () => { clearGtoContext(); stopAllTableSfx(); }, []);
  const isMyTurn = gameState && currentPlayer && 
    gameState.currentPlayerIndex === gameState.players.findIndex(p => p.id === currentUserId) &&
    !currentPlayer.folded && !currentPlayer.allIn && !currentPlayer.eliminated &&
    gameState.status === 'playing' && gameState.round !== 'ended' && gameState.round !== 'showdown';
  const maxBet = gameState ? Math.max(...gameState.players.map(p => p.currentBet || 0)) : 0;
  const callAmount = currentPlayer ? Math.max(0, maxBet - (currentPlayer.currentBet || 0)) : 0;
  const canCheck = callAmount === 0;
  
  // Minimum raise calculation - must match game engine logic
  // The minimum raise is the size of the last raise, or the big blind if no raises yet
  const bigBlind = table?.bigBlind || 2;
  // Use lastRaiseAmount from gameState if available, otherwise calculate from maxBet
  const lastRaise = gameState?.lastRaiseAmount || (maxBet > bigBlind ? maxBet - bigBlind : bigBlind);
  const minRaise = Math.max(lastRaise, bigBlind);
  
  const canRaise = currentPlayer && currentPlayer.chips > callAmount;

  // Pot-fraction raise sizing: a "½ pot" raise is half of (pot AFTER you
  // call) added on top of the call. Raw pot/2 under-sizes into an illegal
  // raise whenever there's a bet to call (e.g. pot 300 facing 200 → 150,
  // below the 200 minimum). Result is the raise-on-top amount the slider
  // uses, clamped to the legal range.
  const potFractionRaise = (f: number): number => {
    if (!gameState || !currentPlayer) return 0;
    const inc = Math.round(f * ((gameState.pot || 0) + callAmount));
    return Math.min(Math.max(inc, minRaise), currentPlayer.chips);
  };

  // Transform players for SVG display
  const playersForSVG = gameState?.players.map(player => ({
    ...player,
    name: usernameMap[player.id] || player.id.substring(0, 8),
    // Training bots get the same PSS character art as live-table bots
    photoURL: String(player.id).startsWith('bot_')
      ? pssBustForBot(player.id)
      : (player as any).photoURL,
  })) || [];

  //  Play sound when it's your turn

  useEffect(() => {
    if (isMyTurn) {
      playSound('your-turn', soundEnabled);
    }
  }, [isMyTurn, soundEnabled]);

  //  Play sound when round changes (cards dealt)

  useEffect(() => {
    if (gameState?.round && gameState.round !== lastRoundRef.current) {
      const prevRound = lastRoundRef.current;
      lastRoundRef.current = gameState.round;
      
      // Update activity time
      setLastActivityTime(Date.now());
      setIsStuck(false);
      
      if (prevRound !== null) {
        if (gameState.round === 'flop' || gameState.round === 'turn' || gameState.round === 'river') {
          playSound('card-deal', soundEnabled);
        } else if (gameState.round === 'preflop') {
          playSound('hand-start', soundEnabled);
        } else if (gameState.round === 'ended' || gameState.round === 'showdown') {
          // All-in hands: the runout animation plays the result sound at the
          // reveal's END - playing it here spoiled the result at street one.
          // The win jingle is for the player who won; everyone else who was
          // still in the hand gets a quiet lose cue, folded players nothing.
          if (!(gameState as any).allInShowdown) {
            playHandResultSfx(gameState.winners as any, gameState.players as any, currentUserId, soundEnabled);
          }
        }
      }
    }
  }, [gameState?.round, soundEnabled]);

  // ============================================================================
  // 🆕 WATCHDOG TIMER - Detects stuck game states
  // ============================================================================
  useEffect(() => {
    if (!gameState || gameState.status !== 'playing') return;

    const watchdog = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityTime;

      // Check if stuck in 'ended' state (next hand should start in 5s, give it 8s)
      if ((gameState.round === 'ended' || gameState.round === 'showdown') && timeSinceActivity > 8000) {
        console.warn(`⚠️ [Watchdog] Hand ended ${Math.round(timeSinceActivity/1000)}s ago but next hand not starting!`);
        setIsStuck(true);
        return;
      }

      // Check if game seems stuck during play
      if (gameState.round !== 'ended' && gameState.round !== 'showdown' && timeSinceActivity > STUCK_DETECTION_TIME) {
        console.warn(`⚠️ [Watchdog] No activity for ${timeSinceActivity}ms - game may be stuck`);
        setIsStuck(true);
      }

      // Check if bot turn is taking too long
      if (botTurnStartRef.current && (now - botTurnStartRef.current) > BOT_TURN_TIMEOUT) {
        console.warn(`⚠️ [Watchdog] Bot turn timeout - forcing reset`);
        isProcessingBotRef.current = false;
        botTurnStartRef.current = null;
        setIsStuck(true);
      }
    };

    watchdogTimerRef.current = setInterval(watchdog, WATCHDOG_INTERVAL);

    return () => {
      if (watchdogTimerRef.current) {
        clearInterval(watchdogTimerRef.current);
      }
    };
  }, [gameState?.status, gameState?.round, lastActivityTime, gameState]);

  // ============================================================================
  // 🆕 FORCE RECOVERY - Manual refresh for stuck games
  // ============================================================================
  const handleForceRecovery = useCallback(async () => {
    if (!gameState || !tableId || !table || isRecovering) return;

    setIsRecovering(true);
    console.log('🔄 [Recovery] Attempting to recover stuck game...');

    try {
      let recoveredState = { ...gameState };

      // Reset processing flags
      isProcessingBotRef.current = false;
      botTurnStartRef.current = null;

      // Case 1: Game ended but next hand didn't start
      if (recoveredState.round === 'ended' || recoveredState.round === 'showdown') {
        console.log('🔄 [Recovery] Hand ended - forcing next hand');
        
        // Get players with chips remaining
        const activePlayers = recoveredState.players.filter(p => !p.eliminated && p.chips > 0);
        
        if (activePlayers.length >= 2) {
          const playersForNewHand = activePlayers.map(p => ({
            id: p.id,
            chips: p.chips,
            seat: p.seat,
            buyIn: p.chips,
          }));

          const newDealer = ((recoveredState.dealerButton || 0) + 1) % activePlayers.length;

          // Dealer goes into init - blinds post once, relative to the real
          // button (posting again here double-charged a second seat pair).
          recoveredState = gameEngine.initializeGame(
            playersForNewHand,
            table.smallBlind,
            table.bigBlind,
            tableId,
            newDealer
          );

          recoveredState.handNumber = (gameState.handNumber || 1) + 1;
          recoveredState.winners = undefined;
          recoveredState.handResult = undefined;

          console.log(`✅ [Recovery] Started hand #${recoveredState.handNumber}`);
          playSound('hand-start', soundEnabled);
        } else {
          toast({
            title: 'Game Over',
            description: 'Not enough players with chips to continue.',
            status: 'info',
            duration: 3000,
          });
          setIsRecovering(false);
          return;
        }
      }
      // Case 2: Stuck during betting - try to progress
      else if (gameEngine.isBettingRoundComplete && gameEngine.isBettingRoundComplete(recoveredState)) {
        console.log('🔄 [Recovery] Betting complete but stuck - progressing round');
        recoveredState = gameEngine.nextRound(recoveredState);
      }
      // Case 3: Current player might be stuck - move to next valid player
      else {
        console.log('🔄 [Recovery] Refreshing current state');
        // Just refresh the state to trigger bot logic
      }

      await updateTrainingGameState(tableId, recoveredState);
      setGameState(recoveredState);
      setLastActivityTime(Date.now());
      setIsStuck(false);

      toast({
        title: 'Game Recovered',

        description: 'The game has been unstuck.',
        status: 'success',
        duration: 2000,
      });

    } catch (err) {
      console.error('Recovery failed:', err);
      toast({
        title: 'Recovery Failed',
        description: 'Please try leaving and rejoining the table.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsRecovering(false);
    }
  }, [gameState, tableId, table, isRecovering, soundEnabled, toast]);

  // ============================================================================
  // TRACK HAND OUTCOME - Updates decision logger with hand results
  // ============================================================================
  const trackHandOutcome = useCallback(async (endedState: GameState) => {
    if (!endedState.winners || endedState.winners.length === 0) return;
    
    const handNumber = endedState.handNumber || 1;
    const winnerId = endedState.winners[0].playerId;
    const humanWon = winnerId === currentUserId;
    
    // Determine if it was a showdown (more than 1 player didn't fold)
    const playersAtShowdown = endedState.players.filter(p => !p.folded && !p.eliminated).length;
    const wasShowdown = playersAtShowdown > 1 && !endedState.handResult?.allOthersFolded;
    
    // Calculate profit/loss for human player
    const humanPlayer = endedState.players.find(p => p.id === currentUserId);
    const startingChips = table?.startingStack ? table.startingStack * (table?.bigBlind || 2) : 400;
    const profitLoss = humanPlayer ? humanPlayer.chips - startingChips : 0;
    
    // Update the decision logger
    trainingDecisionLogger.updateHandOutcome(handNumber, humanWon, wasShowdown, profitLoss);
    console.log(`📊 [Training] Hand #${handNumber} outcome: ${humanWon ? 'WON' : 'LOST'}, showdown: ${wasShowdown}`);
    
    //  FIXED: Record hand for PSS Coins rewards - NOW PROPERLY AWAITED

    try {
      const result = await recordHandPlayed(1);
      if (result.success) {
        console.log(`✅ [Training] Hand recorded - XP: +${result.xpEarned}, Total XP: ${result.newXP}`);
        
        // Show toast for level up
        if (result.leveledUp) {
          toast({
            title: 'Level Up!',

            description: `You've reached ${result.newLevel}!`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
        
        // Show toast for badges
        if (result.newBadges && result.newBadges.length > 0) {
          for (const badge of result.newBadges) {
            toast({
              title: `Badge Earned: ${badge.name}`,

              description: badge.description,
              status: 'success',
              duration: 4000,
              isClosable: true,
            });
          }
        }
      }
    } catch (err: any) {
      console.error('❌ [Training] Failed to record hand played:', err);
      toast({
        title: 'Progress Not Saved',
        description: 'Your hand was played but progress tracking failed.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [currentUserId, table, recordHandPlayed, toast]);

  // ============================================================================
  // SCHEDULE NEXT HAND - Must be defined BEFORE checkAndProgressRound!
  // ============================================================================
  const scheduleNextHand = useCallback((endedState: GameState) => {
    if (!tableId || !table) {
      console.error('❌ [Training] Cannot schedule next hand - missing tableId or table');
      return;
    }

    // Track the hand outcome for analysis (async - has its own error handling)
    trackHandOutcome(endedState).catch(err => console.error('❌ [Training] trackHandOutcome error:', err));

    // 🏆 CHALLENGE MODE: dealing continuation rules. Per-player win/lose
    // settling lives in a subscription-driven effect (co-op safe); THIS
    // block only decides whether the TABLE keeps dealing:
    //   - stop when the hand budget is exhausted
    //   - stop when no human seat has chips left
    //   - solo convenience: stop once the only human has settled
    const ch = (table as any)?.challenge;
    if (ch) {
      const handsPlayed = endedState.handNumber || 1;
      const humanIds = new Set(table?.players || []);
      const humansWithChips = endedState.players.filter(
        p => humanIds.has(p.id) && (p.chips || 0) > 0
      );
      if (handsPlayed >= ch.maxHands) {
        console.log(`⏱️ [CHALLENGE] Hand budget exhausted (${handsPlayed}/${ch.maxHands}) - table stops dealing`);
        return;
      }
      if (humansWithChips.length === 0) {
        console.log('💀 [CHALLENGE] No human has chips - table stops dealing');
        return;
      }
      if ((table?.players?.length || 0) <= 1 && challengeSettledRef.current) {
        console.log('🏁 [CHALLENGE] Solo player settled - table stops dealing');
        return;
      }
    }

    console.log('🔄 [Training] Scheduling next hand in 5 seconds...');
    
    // Clear any existing timer
    if (nextHandTimerRef.current) {
      clearTimeout(nextHandTimerRef.current);
    }
    
    nextHandTimerRef.current = setTimeout(async () => {
      try {
        // Update activity time
        setLastActivityTime(Date.now());
        
        // Get players with chips remaining
        const activePlayers = endedState.players.filter(p => !p.eliminated && p.chips > 0);
        let refillChipsAdded = 0;

        // 🔁 TOURNAMENT REFILL (challenge tables): a busted bot's seat gets a
        // fresh player "moved from another table", exactly like a live
        // tournament backfilling seats. The newcomer buys in at the
        // challenge's bot stack - the busted bot's losses stay lost, which
        // keeps the hero's wins meaningful.
        if (ch && table?.bots?.length) {
          const bustedBots = endedState.players.filter(p =>
            (p.chips || 0) <= 0 && table.bots.some(b => b.uid === p.id)
          );
          if (bustedBots.length > 0) {
            const replacements = createBots(bustedBots.length, table.botDifficulty);
            bustedBots.forEach((busted, i) => {
              const nb = replacements[i];
              const buyIn = ch.botChips || table.startingStack * table.bigBlind;
              refillChipsAdded += buyIn;
              activePlayers.push({
                id: nb.uid,
                chips: buyIn,
                seat: busted.seat,
              } as any);
              console.log(`🔁 [CHALLENGE] ${nb.username} takes the busted seat ${busted.seat}`);
            });
            // Update the roster so bot processing and name display pick up
            // the newcomers (latency-compensated write).
            const survivingBots = table.bots.filter(b => !bustedBots.some(x => x.id === b.uid));
            updateTrainingTableBots(tableId, [...survivingBots, ...replacements]);
          }
        }

        if (activePlayers.length < 2) {
          console.log('⚠️ [Training] Not enough players for next hand');
          toast({
            title: 'Game Over',
            description: 'Not enough players with chips to continue.',
            status: 'info',
            duration: 3000,
          });
          return;
        }

        // Prepare players for new hand
        const playersForNewHand = activePlayers.map(p => ({
          id: p.id,
          chips: p.chips,
          seat: p.seat,
          buyIn: p.chips,
        }));

        // Rotate dealer
        const previousDealer = endedState.dealerButton || 0;
        const newDealer = (previousDealer + 1) % activePlayers.length;

        // 📈 Blind escalation: challenge tables play like a turbo - blinds
        // jump every few hands, exactly like the real tournament clock.
        const nextHandNumber = (endedState.handNumber || 1) + 1;
        const { sb: nextSb, bb: nextBb, ante: nextAnte, level: nextLevel } = getBlindsForHand(nextHandNumber);
        if ((table as any)?.challenge) {
          const prevLevel = getBlindsForHand(endedState.handNumber || 1).level;
          if (nextLevel > prevLevel) {
            playSound('your-turn', soundEnabled);
            toast({
              title: `Blinds up! Level ${nextLevel}`,
              description: `${nextSb}/${nextBb}${nextAnte ? ` · ante ${nextAnte}` : ''}`,
              status: 'warning',
              duration: 3000,
            });
          }
        }

        // Initialize new game. The rotated dealer goes IN so cards and
        // blinds post relative to the real button in one pass - the old
        // flow posted blinds inside init (dealer 0) and then AGAIN here
        // with the new dealer: two seat-pairs paid every hand while the
        // pot assignment hid the extra 1.5bb. That leak surfaced the
        // moment settlement switched to contribution-based side pots.
        const newGameState = gameEngine.initializeGame(
          playersForNewHand,
          nextSb,
          nextBb,
          tableId,
          newDealer
        );

        newGameState.handNumber = (endedState.handNumber || 1) + 1;
        newGameState.winners = undefined;
        newGameState.handResult = undefined;

        // 🧮 Carry the chip-conservation baseline forward; refill buy-ins
        // are the only legitimate new chips. Busted players who dropped out
        // hold zero, so the total is unchanged by their departure.
        const prevTotal = typeof (endedState as any).chipTotal === 'number'
          ? (endedState as any).chipTotal
          : endedState.players.reduce((s, p) => s + (p.chips || 0), 0) + (endedState.pot || 0);
        (newGameState as any).chipTotal = prevTotal + refillChipsAdded;

        // Clear runout suspense state for the fresh hand
        setTrainingRunoutPhase('none');
        setTrainingEquities({});
        setRunoutBoardCount(0);
        setRunoutPotDisplay(null);

        // 💰 Post antes (dead money from every player, like the real
        // tournament levels). Doesn't count toward currentBet - it just
        // seeds the pot and makes steals worth more each level.
        if (nextAnte > 0) {
          for (const p of newGameState.players) {
            if (!p || p.eliminated || (p.chips || 0) <= 0) continue;
            const posted = Math.min(nextAnte, p.chips);
            p.chips -= posted;
            p.totalBetThisHand = (p.totalBetThisHand || 0) + posted;
            newGameState.pot = (newGameState.pot || 0) + posted;
            if (p.chips === 0) p.allIn = true;
          }
          (newGameState as any).ante = nextAnte;
        }

        console.log(`✅ [Training] Hand #${newGameState.handNumber} started`);
        // The round-change effect plays the deal cue for every client
        // (including this one) when the new preflop state lands.

        await updateTrainingGameState(tableId, newGameState);
        setGameState(newGameState);
        
      } catch (err) {
        console.error('Error starting new hand:', err);
        setIsStuck(true);
      }
    }, 5000);
  }, [tableId, table, toast, soundEnabled, trackHandOutcome, currentUserId, getBlindsForHand]);

  // ============================================================================
  // ROUND PROGRESSION LOGIC - THE FIX: Handle 'ended' directly from nextRound!
  // ============================================================================
  
  const checkAndProgressRound = useCallback(async (currentState: GameState): Promise<GameState> => {
    if (!tableId || !table) return currentState;
    
    let state = { ...currentState };
    
    // Update activity time
    setLastActivityTime(Date.now());
    setIsStuck(false);
    
    // Skip if already ended or in showdown
    if (state.round === 'ended' || state.round === 'showdown') {
      return state;
    }

    // Check for single player remaining (walkover win)
    const activePlayers = state.players.filter(p => !p.folded && !p.eliminated);
    if (activePlayers.length === 1) {
      console.log('🏆 [Training] Single player remaining - walkover win');
      const winner = activePlayers[0];
      
      // Award pot to winner
      const winnerIdx = state.players.findIndex(p => p.id === winner.id);
      state.players[winnerIdx].chips += state.pot;
      
      state.winners = [{
        playerId: winner.id,
        amount: state.pot,
        hand: 'All others folded',
      }];
      state.handResult = {
        winnerId: winner.id,
        winnerName: usernameMap[winner.id] || winner.id,
        potWon: state.pot,
        allOthersFolded: true,
      };
      state.round = 'ended';
      state.pot = 0;

      // Result sound comes from the round-change effect (win only if you won)

      await updateTrainingGameState(tableId, state);
      scheduleNextHand(state);
      return state;
    }

    // Check if all remaining players are all-in (no more betting possible).
    // ⚠️ CRITICAL: a lone remaining actor must have MATCHED the top bet
    // before this fires. A fresh jam is a bet the other player is still
    // entitled to call or fold to - short-circuiting here ran the board
    // out before the caller ever got their turn, so 'called' jams were
    // settled at the caller's OLD bet and the jammer's excess came back
    // as an uncalled refund ('I doubled up but only won part of it').
    const playersWhoCanAct = activePlayers.filter(p => !p.allIn && p.chips > 0);
    const maxBetLive = Math.max(...activePlayers.map(p => p.currentBet || 0));
    const allBetsSettled = playersWhoCanAct.every(
      p => p.hasActedThisRound && (p.currentBet || 0) >= maxBetLive
    );
    if (activePlayers.length > 1 && playersWhoCanAct.length <= 1 && allBetsSettled) {
      // 🎬 All-in showdown - REAL-TABLE PATTERN: resolve the whole hand in
      // one write, and let every client animate the runout locally (cards
      // up, equities ticking, streets landing one at a time, winner gated
      // to the end). No staged intermediate writes - those raced with the
      // subscription and only the writing client ever saw the suspense.
      if (runoutStagingRef.current) {
        return state;
      }
      runoutStagingRef.current = true;

      try {
        console.log('🃏 [Training] All-in showdown - resolving hand for local runout animation');
        (state as any).allInShowdown = true;
        (state as any).allInOccurred = true;
        // Tell every client where the animation starts and what the pot
        // was before it gets zeroed by the award below.
        (state as any).runoutFromBoardCount = state.communityCards.length;
        (state as any).runoutPot = state.pot;

        // Deal the remaining streets instantly - the ANIMATION paces the
        // reveal, not the game state. The engine's all-in branch inside
        // nextRound deals the full board AND settles the pot via
        // determineWinners (chips awarded, winners + handResult set).
        while (state.communityCards.length < 5) {
          state = gameEngine.nextRound(state);
          (state as any).allInShowdown = true;
          (state as any).allInOccurred = true;
        }

        // ⚠️ Do NOT award again here: the engine already paid the winners
        // above. The old second award minted a duplicate pot every all-in,
        // which is how bot stacks ballooned past the chip total in play.
        // Fallback settlement (river all-ins where the loop never ran) goes
        // through the engine too - its layered side pots refund uncalled
        // excess, which the old flat evaluator gave to the winner.
        if (!state.winners || state.winners.length === 0) {
          gameEngine.determineWinners(state);
        }
        // Fill in the display name the engine leaves blank.
        if (state.handResult && !state.handResult.winnerName) {
          state.handResult.winnerName =
            usernameMap[state.handResult.winnerId] || state.handResult.winnerId;
        } else if (!state.handResult && state.winners && state.winners.length > 0) {
          const w0: any = state.winners[0];
          state.handResult = {
            winnerId: w0.playerId,
            winnerName: usernameMap[w0.playerId] || w0.playerId,
            winningHand: w0.hand ? { type: w0.hand } : undefined,
            potWon: w0.amount,
          } as any;
        }

        state.round = 'ended';
        state.pot = 0;

        await updateTrainingGameState(tableId, state);
        // No scheduleNextHand here: the runout animation effect schedules
        // the next hand (host only) once the reveal finishes on screen.
      } finally {
        runoutStagingRef.current = false;
      }
      return state;
    }

    // Check if betting round is complete
    if (!gameEngine.isBettingRoundComplete(state)) {
      return state; // Still waiting for actions
    }

    console.log(`✅ [Training] Betting complete for ${state.round} - progressing`);

    // Progress to next round
    const previousRound = state.round;
    const cardsBeforeProgress = (state.communityCards || []).length;
    const potBeforeProgress = state.pot || 0;
    state = gameEngine.nextRound(state);
    console.log(`🎯 [Training] ${previousRound} → ${state.round}`);
    // Street sound comes from the round-change effect once the state lands
    // (playing it here as well doubled every flop, turn and river).

    //  THE FIX: Handle BOTH 'showdown' AND 'ended' from nextRound!

    if (state.round === 'showdown' || state.round === 'ended') {
      console.log('🎰 [Training] Hand complete - evaluating hands');

      // The engine can resolve an all-in INTERNALLY inside nextRound (deal
      // the remaining board + settle in one step). Mark it so this hand
      // gets the staged runout animation instead of an instant reveal with
      // final percentages - the second path the polish pass missed.
      const liveAtEnd = state.players.filter(p => !p.folded && !p.eliminated);
      const engineAllIn = liveAtEnd.length > 1 && liveAtEnd.some(p => p.allIn)
        && (state.communityCards || []).length > cardsBeforeProgress + 1;
      if (engineAllIn && !(state as any).allInShowdown) {
        (state as any).allInShowdown = true;
        (state as any).allInOccurred = true;
        (state as any).runoutFromBoardCount = cardsBeforeProgress;
        (state as any).runoutPot = potBeforeProgress;
      }

      // Only settle if the engine hasn't already - and settle THROUGH the
      // engine so layered side pots and uncalled-bet refunds apply.
      if (!state.winners || state.winners.length === 0) {
        gameEngine.determineWinners(state);
      }
      if (state.handResult && !state.handResult.winnerName) {
        state.handResult.winnerName =
          usernameMap[state.handResult.winnerId] || state.handResult.winnerId;
      }

      state.round = 'ended';
      state.pot = 0;

      // All-in hands: the runout animation owns the result sound AND the
      // next-hand scheduling (after the reveal finishes). Otherwise the
      // round-change effect plays win or lose once the state lands.

      await updateTrainingGameState(tableId, state);
      if (!(state as any).allInShowdown) {
        scheduleNextHand(state);
      }

      return state;
    }

    // Save intermediate state and continue checking
    await updateTrainingGameState(tableId, state);
    
    // Recursively check if next round is also complete (e.g., all players all-in)
    return checkAndProgressRound(state);
  }, [tableId, table, usernameMap, soundEnabled, scheduleNextHand, currentUserId]);

  // Subscribe to table updates
  useEffect(() => {
    if (!tableId || !currentUserId) {
      setError('Invalid table ID or user');
      setIsLoading(false);
      return;
    }

    // Never bounce on the FIRST empty snapshot. Table creation is
    // latency-compensated (fire-and-forget write), so on a slow phone
    // connection this page can mount and receive a server-first "no doc"
    // reading - or a transient subscription error, which the service also
    // maps to null - BEFORE the create lands. The old code navigated
    // straight back to the lobby on any null, which made "Play Live"
    // look completely broken. Only a table we have SEEN existing and then
    // vanishing means deletion; otherwise keep loading and, if nothing
    // ever arrives, show a real error with a way back.
    let seenTable = false;
    const mountedAt = Date.now();
    const noTableTimeout = setTimeout(() => {
      if (!seenTable) {
        setError('Could not load this training table. Check your connection and try again.');
        setIsLoading(false);
      }
    }, 10000);

    const unsubscribe = subscribeToTrainingTable(tableId, (tableData) => {
      if (tableData === null) {
        if (seenTable) {
          // Genuine deletion of a table we were at
          navigate('/training');
        } else if (Date.now() - mountedAt > 8000) {
          setError('Could not load this training table. Check your connection and try again.');
          setIsLoading(false);
        }
        // else: creation still in flight - keep the loading spinner
        return;
      }

      seenTable = true;
      clearTimeout(noTableTimeout);
      setTable(tableData);
      if (tableData.gameState) {
        setGameState(tableData.gameState as GameState);
        setLastActivityTime(Date.now());
      }
      setIsLoading(false);
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      clearTimeout(noTableTimeout);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (botTimerRef.current) {
        clearTimeout(botTimerRef.current);
      }
      if (nextHandTimerRef.current) {
        clearTimeout(nextHandTimerRef.current);
      }
      if (watchdogTimerRef.current) {
        clearInterval(watchdogTimerRef.current);
      }
      // Leaving the page kills any playing audio - no victory music
      // following the player into the lobby.
      stopAllSounds();
    };
  }, [tableId, currentUserId, navigate]);

  // ============================================================================
  // 🎬 ALL-IN RUNOUT ANIMATION - the real-table pattern, ported from the
  // live tables: the hand is resolved in ONE write; every client (host and
  // co-op friends alike) then animates the reveal locally. Cards face up
  // with win percentages, a beat to sweat, streets landing one at a time
  // with the equities re-ticking, and the winner held back until the river.
  // useLayoutEffect so the first staged frame paints BEFORE the browser can
  // flash the fully-dealt board that just arrived from the subscription.
  // ============================================================================
  useLayoutEffect(() => {
    const gs = gameState;
    if (!gs) return;

    // Fresh hand (or a non-all-in hand): clear any leftover animation state.
    if (!(gs as any).allInShowdown) {
      setTrainingRunoutPhase(prev => (prev !== 'none' ? 'none' : prev));
      setRunoutPotDisplay(prev => (prev !== null ? null : prev));
      return;
    }

    if (gs.round !== 'ended' || !gs.winners || gs.winners.length === 0) return;
    const handNo = gs.handNumber || 1;
    if (lastRunoutAnimatedRef.current === handNo) return;
    lastRunoutAnimatedRef.current = handNo;

    const board = gs.communityCards || [];
    // Missing marker = animate from the start; falling back to the full
    // board length skipped the reveal and flashed final percentages.
    const fromCount = Math.min(
      typeof (gs as any).runoutFromBoardCount === 'number' ? (gs as any).runoutFromBoardCount : 0,
      board.length
    );
    const potForDisplay = (gs as any).runoutPot
      || gs.winners.reduce((s: number, w: any) => s + (w?.amount || 0), 0);
    const heroSeat = gs.players.find(p => p.id === currentUserId);
    const heroInHand = !!heroSeat && !heroSeat.folded && !heroSeat.eliminated;
    const revealPause = heroInHand ? 1600 : 300;
    const streetPause = heroInHand ? 1800 : 350;

    // Streets still to land: flop as a unit, then turn, then river.
    const stops: number[] = [];
    if (fromCount < 3) stops.push(3);
    if (fromCount < 4) stops.push(4);
    if (fromCount < 5) stops.push(5);

    const sleep = (ms: number) => new Promise<void>(res => setTimeout(res, ms));
    (async () => {
      // 1) Cards up + equities - the sweat moment.
      setRunoutPotDisplay(potForDisplay);
      setRunoutBoardCount(fromCount);
      setTrainingRunoutPhase('running');
      setTrainingEquities(computeEquitiesAtStreet(gs, fromCount));
      setLastActivityTime(Date.now());
      botTurnStartRef.current = null;
      await sleep(revealPause);

      // 2) Streets, one at a time, equity re-ticking after each. Extra beat
      //    on the river - the decisive card deserves it.
      for (const n of stops) {
        setRunoutBoardCount(n);
        playSound('card-deal', soundEnabled);
        setTrainingEquities(computeEquitiesAtStreet(gs, n));
        setLastActivityTime(Date.now());
        botTurnStartRef.current = null;
        await sleep(n >= 5 ? Math.round(streetPause * 1.2) : streetPause);
      }

      // 3) Winner - the table SVG holds the winner display until this flips.
      setTrainingRunoutPhase('complete');
      setRunoutPotDisplay(null);
      playHandResultSfx(gs.winners as any, gs.players as any, currentUserId, soundEnabled);

      // The host deals the next hand once the show is over (its own 5s
      // countdown keeps the result on screen; slightly-behind clients
      // finish their animation inside that window).
      if (table?.hostId === currentUserId) {
        scheduleNextHand(gs);
      }
    })();
    // Intentionally NO cleanup cancellation: subscription echoes re-run this
    // effect, and cancelling mid-flight would freeze the reveal. The
    // lastRunoutAnimatedRef guard makes re-entry a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, currentUserId, soundEnabled, table?.hostId, scheduleNextHand]);

  // Keep the latest state visible to long-lived timers.
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);

  // 🧮 CHIP AUDIT: stacks + pot must always equal the table's baseline.
  // Bets move chips into the pot instantly and settlement moves the pot
  // into stacks, so ANY drift at ANY rendered moment is a real bug - log
  // it loudly with the full context needed to fix it.
  useEffect(() => {
    const gs = gameState;
    const expected = (gs as any)?.chipTotal;
    if (!gs || typeof expected !== 'number') return;
    const stacks = gs.players.reduce((s, p) => s + (p.chips || 0), 0);
    const actual = stacks + (gs.pot || 0);
    if (actual !== expected) {
      console.error(
        `🧮 [CHIP AUDIT] DRIFT ${actual - expected > 0 ? '+' : ''}${actual - expected} ` +
        `on hand ${gs.handNumber} (${gs.round}): stacks ${stacks} + pot ${gs.pot || 0} = ${actual}, expected ${expected}. ` +
        `Stacks: ${gs.players.map(p => `${p.id.slice(0, 10)}=${p.chips}`).join(', ')}`
      );
    }
  }, [gameState]);

  // 🚑 DEAL WATCHDOG: if a finished hand is still sitting undealt after the
  // runout + schedule window, the next-hand timer died (whatever the cause)
  // - force the deal instead of stranding the table. Host only, once per
  // hand, and never once the run is over.
  useEffect(() => {
    if (!gameState || gameState.round !== 'ended') return;
    if (!table || table.hostId !== currentUserId) return;
    const ch = (table as any)?.challenge;
    const endedHand = gameState.handNumber || 1;
    if (ch && endedHand >= ch.maxHands) return;
    if (challengeSettledRef.current && (table.players?.length || 0) <= 1) return;
    if (lastForcedDealHandRef.current >= endedHand) return;

    const timer = setTimeout(() => {
      const latest = gameStateRef.current;
      if (latest && latest.round === 'ended' && (latest.handNumber || 1) === endedHand) {
        console.error(`🚑 [DEAL WATCHDOG] Hand #${endedHand} finished but no deal followed - forcing next hand. If you see this, please report it.`);
        lastForcedDealHandRef.current = endedHand;
        scheduleNextHand(latest);
      }
    }, 15000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState?.round, gameState?.handNumber, table?.hostId, currentUserId]);

  // Process bot turn with timeout protection
  const processBotTurn = useCallback(async () => {
    if (!gameState || !table || !tableId || isProcessingBotRef.current) return;
    if (gameState.status !== 'playing' || gameState.round === 'ended' || gameState.round === 'showdown') return;

    const currentPlayerInGame = gameState.players[gameState.currentPlayerIndex];
    if (!currentPlayerInGame) return;

    // Check if current player is a bot
    const bot = table.bots.find(b => b.uid === currentPlayerInGame.id);
    if (!bot) return; // Not a bot's turn

    isProcessingBotRef.current = true;
    botTurnStartRef.current = Date.now(); // Track when bot turn started

    try {
      // Get bot's thinking delay (with max cap)
      const delay = Math.min(getBotThinkingDelay(bot.difficulty), 5000);
      await new Promise(resolve => setTimeout(resolve, delay));

      // Double-check we're still processing (might have been reset by watchdog)
      if (!isProcessingBotRef.current) {
        console.log('⚠️ [Bot] Processing was reset, aborting');
        return;
      }

      // Calculate current bet (highest bet on the table)
      const currentBet = Math.max(...gameState.players.map(p => p.currentBet || 0));
      
      // Make bot decision with correct parameters
      const decision = makeBotDecision({
        bot,
        player: currentPlayerInGame,
        gameState,
        seatIndex: gameState.currentPlayerIndex,
        dealerIndex: gameState.dealerButton,
        minBet: table.bigBlind,
        currentBet,
        potSize: gameState.pot,
      });
      
      console.log(`🤖 Bot ${bot.username} decides: ${decision.action}${decision.amount ? ` $${decision.amount}` : ''}`);

      // Update activity time
      setLastActivityTime(Date.now());
      setIsStuck(false);

      //  Play sound for bot action

      if (decision.action === 'fold') playSound('fold', soundEnabled);
      else if (decision.action === 'check') playSound('check', soundEnabled);
      else if (decision.action === 'call') playSound('chip-single', soundEnabled);
      else if (decision.action === 'bet' || decision.action === 'raise') playSound('raise', soundEnabled);
      else if (decision.action === 'all-in') playSound('all-in', soundEnabled);

      // Convert bot action to game engine action
      const gameAction = decision.action === 'bet' ? 'raise' : decision.action;
      const actionAmount = (decision.action === 'bet' || decision.action === 'raise') 
        ? (decision.amount || table.bigBlind) 
        : 0;

      // Apply the action using playerAction method
      let newGameState = gameEngine.playerAction(
        gameState,
        currentPlayerInGame.id,
        gameAction as 'fold' | 'check' | 'call' | 'raise' | 'all-in',
        actionAmount
      );

      //  KEY FIX: Check and progress round after action

      newGameState = await checkAndProgressRound(newGameState);

      setGameState(newGameState);

    } catch (err) {
      console.error('Bot action error:', err);
      setIsStuck(true);
    } finally {
      isProcessingBotRef.current = false;
      botTurnStartRef.current = null;
    }
  }, [gameState, table, tableId, checkAndProgressRound, soundEnabled]);

  // Watch for bot turns.
  // HOST-ONLY: in co-op every client receives the same state via the table
  // subscription - if all of them drove bots, actions would double-fire and
  // corrupt the hand. The host's client is the single bot engine.
  useEffect(() => {
    if (!gameState || gameState.status !== 'playing') return;
    if (gameState.round === 'ended' || gameState.round === 'showdown') return;
    if (!isHost) return;
    if (isPaused) return; // ⏸ paused - bots wait too

    const currentPlayerInGame = gameState.players[gameState.currentPlayerIndex];
    if (!currentPlayerInGame || !table) return;

    // Check if current player is a bot
    const isBot = table.bots.some(b => b.uid === currentPlayerInGame.id);

    if (isBot && !isProcessingBotRef.current) {
      // Schedule bot action
      botTimerRef.current = setTimeout(() => {
        if (isPausedRef.current) return;
        processBotTurn();
      }, 500);
    }

    return () => {
      if (botTimerRef.current) {
        clearTimeout(botTimerRef.current);
      }
    };
  }, [gameState?.currentPlayerIndex, gameState?.status, gameState?.round, processBotTurn, table, isHost, isPaused]);

  // Handle player action
  // Latest-action ref so the turn-timer interval can auto-act without
  // recreating the interval on every render.
  const handleActionRef = useRef<((action: PlayerAction, amount?: number) => Promise<void>) | null>(null);

  // ⏱ TURN TIMER - runs while it's the human's turn; auto-check when free,
  // auto-fold otherwise. Paused during the mission briefing.
  useEffect(() => {
    if (turnTimerIntervalRef.current) {
      clearInterval(turnTimerIntervalRef.current);
      turnTimerIntervalRef.current = null;
    }
    if (!gameState || gameState.round === 'ended' || gameState.round === 'showdown') return;
    if (challengeResult) return;
    if (isPaused) return; // ⏸ paused - clock frozen
    if ((table as any)?.challenge && !briefingDismissed) return; // clock waits for the briefing
    const activePlayer = gameState.players[gameState.currentPlayerIndex];
    if (!activePlayer || activePlayer.id !== currentUserId) return;

    setTurnTimeLeft(TRAINING_TURN_SECONDS);
    turnTimerIntervalRef.current = setInterval(() => {
      setTurnTimeLeft(prev => {
        if (prev <= 1) {
          if (turnTimerIntervalRef.current) {
            clearInterval(turnTimerIntervalRef.current);
            turnTimerIntervalRef.current = null;
          }
          // Auto-act: free check when possible, fold otherwise - same rule
          // as the live tables.
          const gs = gameState;
          const me = gs.players.find(p => p.id === currentUserId);
          const maxBet = Math.max(0, ...gs.players.map(p => p.currentBet || 0));
          const canCheck = (me?.currentBet || 0) >= maxBet;
          handleActionRef.current?.(canCheck ? 'check' : 'fold').catch(() => {});
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (turnTimerIntervalRef.current) {
        clearInterval(turnTimerIntervalRef.current);
        turnTimerIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState?.currentPlayerIndex, gameState?.handNumber, gameState?.round, gameState?.status, currentUserId, briefingDismissed, challengeResult, isPaused]);

  const handleAction = async (action: PlayerAction, amount?: number) => {
    if (!gameState || !tableId || !table || isProcessingAction) return;
    if (gameState.round === 'ended' || gameState.round === 'showdown') return;

    const activePlayer = gameState.players[gameState.currentPlayerIndex];
    if (activePlayer.id !== currentUserId) {
      toast({
        title: 'Not your turn',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setIsProcessingAction(true);
    setShowRaiseSlider(false);
    setLastActivityTime(Date.now());
    setIsStuck(false);

    //  Play sound for player action

    if (action === 'fold') playSound('fold', soundEnabled);
    else if (action === 'check') playSound('check', soundEnabled);
    else if (action === 'call') playSound('chip-single', soundEnabled);
    else if (action === 'raise') playSound('raise', soundEnabled);
    else if (action === 'all-in') playSound('all-in', soundEnabled);

    try {
      // Get call amount before action
      const currentCallAmount = Math.max(0, (gameState.currentBet || 0) - activePlayer.currentBet);

      // Defensive conversion: a "raise" that commits the whole stack IS an
      // all-in - route it through the engine's all-in handler so the
      // below-min-raise shove is always legal.
      if (action === 'raise' && (amount || 0) >= activePlayer.chips) {
        action = 'all-in';
        amount = 0;
      }

      // Debug logging for raise
      if (action === 'raise') {
        console.log(`🎯 [Raise Debug] amount passed: ${amount}, minRaise: ${minRaise}, maxBet: ${maxBet}`);
      }

      // Apply action using playerAction method
      let newGameState = gameEngine.playerAction(
        gameState,
        activePlayer.id,
        action,
        action === 'raise' ? (amount || minRaise) : 0
      );

      // Log player decision for analysis
      if (sessionStarted) {
        trainingDecisionLogger.logDecision({
          handNumber: gameState.handNumber,
          player: activePlayer,
          seatIndex: gameState.currentPlayerIndex,
          dealerIndex: gameState.dealerButton,
          totalPlayers: gameState.players.length,
          street: gameState.round as any,
          action: action as any,
          // Amount only for chip actions - a fold logged with the call
          // amount produced nonsense like "you chose fold 200".
          betAmount: action === 'raise' ? (amount || minRaise)
            : action === 'all-in' ? activePlayer.chips
            : action === 'call' ? currentCallAmount
            : 0,
          potSize: gameState.pot,
          callAmount: currentCallAmount,
          communityCards: gameState.communityCards,
          wasPreflopRaiser: activePlayer.lastAction === 'raise' && gameState.round === 'preflop',
          wasAggressor: activePlayer.lastAction === 'raise',
          playersInHand: gameState.players.filter(p => !p.folded && !p.eliminated).length,
          effectiveStack: Math.min(
            activePlayer.chips,
            ...gameState.players.filter(p => !p.folded && !p.eliminated && p.id !== activePlayer.id).map(p => p.chips)
          ),
          // Current big blind (challenge blinds escalate) so the coach can
          // compute exact stack depth for push/fold analysis.
          bigBlind: getBlindsForHand(gameState.handNumber || 1).bb,
        });
      }

      //  KEY FIX: Check and progress round after action

      newGameState = await checkAndProgressRound(newGameState);

      setGameState(newGameState);
      setRaiseAmount(0);

    } catch (err: any) {
      console.error('Action error:', err);
      playSound('error', soundEnabled);
      
      // Show the actual error message to the user
      const errorMessage = err?.message || 'Action failed';
      toast({
        title: 'Invalid Action',
        description: errorMessage,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Keep the turn-timer's auto-act pointed at the freshest handler.
  handleActionRef.current = handleAction;

  // 🏆 PER-CLIENT CHALLENGE SETTLE - subscription-driven so EVERY player
  // (host or joiner) evaluates their own outcome at each hand boundary,
  // regardless of whose client closed the hand. Co-op: your friend busting
  // doesn't end your run; your win doesn't end theirs.
  // ONE evaluation path, on purpose: only at hand end, only on settled
  // stacks. The mid-hand "safety net" variants that briefly lived here
  // could declare a stale win while a later hand was being LOST - layered
  // patches, now removed. Reliability comes from the deps instead: this
  // effect re-runs on every snapshot AND every animation-phase change, so
  // a finished hand cannot slip past unevaluated.
  useEffect(() => {
    const ch = (table as any)?.challenge;
    if (!ch || !gameState || challengeSettledRef.current) return;
    if (gameState.round !== 'ended') return;
    // All-in hand: hold the verdict until the runout animation has landed
    // the river - the result modal must never spoil the sweat. The effect
    // re-runs when the phase flips to 'complete'.
    if ((gameState as any).allInShowdown && trainingRunoutPhase !== 'complete') return;
    const handNum = gameState.handNumber || 1;
    if (lastEvaluatedHandRef.current >= handNum) return; // already evaluated this hand
    lastEvaluatedHandRef.current = handNum;

    const hero = gameState.players.find(p => p.id === currentUserId);
    const heroChips = hero?.chips || 0;
    const goalType = ch.goalType || 'chips';

    // Steal-drill bookkeeping: preflop win = hand ended with zero community
    // cards and this client's player took the pot. Persisted to the table
    // doc so a refresh (or co-op rejoin) doesn't wipe credited progress.
    if (goalType === 'preflopWins') {
      const heroWonPot = gameState.winners?.[0]?.playerId === currentUserId;
      const noFlop = (gameState.communityCards || []).length === 0;
      if (heroWonPot && noFlop) {
        preflopWinsRef.current += 1;
        setPreflopWins(preflopWinsRef.current);
        recordChallengeProgress(tableId || '', currentUserId, { preflopWins: preflopWinsRef.current })
          .catch(() => {});
        console.log(`🥷 [CHALLENGE] Preflop steal ${preflopWinsRef.current}/${ch.goalCount}`);
      }
    }

    const won =
      goalType === 'chips' ? heroChips >= ch.goalChips
      : goalType === 'preflopWins' ? preflopWinsRef.current >= (ch.goalCount || 1)
      : /* survive */ handNum >= ch.maxHands && heroChips >= ch.goalChips;

    // Let the winner banner + river card breathe before the result modal.
    const settle = (r: { outcome: 'won' | 'lost'; heroChips: number; handsPlayed: number }) => {
      challengeSettledRef.current = true;
      // The training report and AI coach get the verdict, not just the hands.
      trainingDecisionLogger.setChallengeOutcome(r.outcome, r.heroChips, r.handsPlayed);
      setTimeout(() => setChallengeResult(r), 2800);
    };

    if (won) {
      console.log(`🏆 [CHALLENGE] WON (${goalType}) in ${handNum} hands`);
      settle({ outcome: 'won', heroChips, handsPlayed: handNum });
    } else if (heroChips <= 0) {
      console.log(`💀 [CHALLENGE] LOST: busted on hand ${handNum}`);
      settle({ outcome: 'lost', heroChips: 0, handsPlayed: handNum });
    } else if (handNum >= ch.maxHands) {
      console.log(`⏱️ [CHALLENGE] LOST: out of hands (${handNum}/${ch.maxHands})`);
      settle({ outcome: 'lost', heroChips, handsPlayed: handNum });
    }
    // Full gameState in deps: EVERY snapshot re-evaluates the win/loss
    // checks (guards make re-runs idempotent). Keying on round/handNumber
    // alone left gaps where chips changed but neither did.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, table, currentUserId, trainingRunoutPhase]);

  // 📊 AUTO-START the decision-logger session when the table arrives already
  // playing. Challenge tables are started by the Tournament Training hub
  // (never through this page's Start button), so without this the logger
  // recorded zero decisions and 'View Training Report' produced nothing.
  // Also covers rejoining/refreshing an in-progress normal table.
  useEffect(() => {
    if (sessionStarted || !table || !gameState) return;
    if (table.status !== 'playing') return;
    trainingDecisionLogger.startSession({
      uid: currentUserId,
      username: username,
      tableId: tableId || '',
      tableName: table.tableName,
      botDifficulty: table.botDifficulty,
      botCount: table.bots.length,
      humanCount: table.players.length,
      blinds: { small: table.smallBlind, big: table.bigBlind },
      startingStack: (table as any)?.challenge
        ? Math.max(1, Math.round(((table as any).challenge.heroChips) / (table.bigBlind || 1)))
        : table.startingStack,
      // Challenge context so the report and AI coach evaluate the hands
      // against THIS challenge's winning approach, not generic play.
      ...((table as any)?.challenge ? {
        challenge: {
          id: (table as any).challenge.id,
          title: (table as any).challenge.title,
          mission: (table as any).challenge.mission,
          tips: (table as any).challenge.tips,
          skill: (table as any).challenge.skill,
          concept: (table as any).challenge.concept,
          goalType: (table as any).challenge.goalType,
          goalChips: (table as any).challenge.goalChips,
          heroChips: (table as any).challenge.heroChips,
          maxHands: (table as any).challenge.maxHands,
        },
      } : {}),
    });
    setSessionStarted(true);
    console.log('📊 [Training] Decision-logger session auto-started (table already playing)');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.status, gameState?.handNumber, sessionStarted]);

  // Start game
  const handleStartGame = async () => {
    if (!tableId || !currentUserId || !table) return;

    setIsStarting(true);
    try {
      // Start training session for logging
      trainingDecisionLogger.startSession({
        uid: currentUserId,
        username: username,
        tableId,
        tableName: table.tableName,
        botDifficulty: table.botDifficulty,
        botCount: table.bots.length,
        humanCount: table.players.length,
        blinds: { small: table.smallBlind, big: table.bigBlind },
        startingStack: table.startingStack,
        ...((table as any)?.challenge ? {
          challenge: {
            id: (table as any).challenge.id,
            title: (table as any).challenge.title,
            mission: (table as any).challenge.mission,
            tips: (table as any).challenge.tips,
            skill: (table as any).challenge.skill,
            concept: (table as any).challenge.concept,
            goalType: (table as any).challenge.goalType,
            goalChips: (table as any).challenge.goalChips,
            heroChips: (table as any).challenge.heroChips,
            maxHands: (table as any).challenge.maxHands,
          },
        } : {}),
      });
      setSessionStarted(true);

      // Pass the subscribed table so the service never round-trips the
      // server before dealing (that stall was the Deal Me In freeze).
      const result = await startTrainingGame(tableId, currentUserId, table);
      if (!result.success) {
        throw new Error(result.error);
      }
      
      setLastActivityTime(Date.now());
      playSound('hand-start', soundEnabled);

      toast({
        title: 'Game Started!',

        status: 'success',
        duration: 2000,
      });
    } catch (err: any) {
      playSound('error', soundEnabled);
      toast({
        title: 'Failed to start game',
        description: err.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsStarting(false);
    }
  };

  // Leave table
  // viewReport=false: leave straight to the lobby. The report is still
  // generated and saved in the background, it just doesn't hijack the exit.
  const handleLeaveTable = async (viewReport: boolean = true) => {
    if (!tableId || !currentUserId) return;

    setIsLeaving(true);
    stopAllSounds();
    try {
      // Pass the subscribed table (no server round-trip) and never let a
      // slow backend strand the player at a table they asked to leave.
      const result = await Promise.race([
        leaveTrainingTable(tableId, currentUserId, table || undefined),
        new Promise<{ success: boolean; report?: any }>(res =>
          setTimeout(() => res({ success: true }), 6000)
        ),
      ]);

      if (viewReport && result.report) {
        toast({
          title: 'Report Generated!',

          description: `Your score: ${result.report.overallScore.toFixed(1)}/10`,
          status: 'success',
          duration: 3000,
        });
        navigate(`/training/report/${result.report.reportId}`);
      } else {
        if (result.report) {
          toast({
            title: 'Session saved',
            description: 'Your training report is available under Training anytime.',
            status: 'info',
            duration: 2500,
          });
        }
        navigate('/training');
      }
    } catch (err) {
      console.error('Leave error:', err);
      navigate('/training');
    } finally {
      setIsLeaving(false);
      setShowLeaveModal(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={POKER_COLORS.darkBg}
      >
        <VStack spacing={4}>
          <Spinner color={POKER_COLORS.cyan} size="xl" />
          <Text color="gray.400">Loading training table...</Text>
        </VStack>
      </Box>
    );
  }

  // Error state
  if (error || !table) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={POKER_COLORS.darkBg}
        p={4}
      >
        <VStack spacing={4}>
          <Text color={POKER_COLORS.darkRed} fontSize="lg">
            {error || 'Table not found'}
          </Text>
          <PokerButton variant="primary" onClick={() => navigate('/training')}>
            Back to Training
          </PokerButton>
        </VStack>
      </Box>
    );
  }

  // Lobby view (waiting to start). Challenge tables skip the invite lobby -
  // they go straight to the mission briefing, and dealing starts on
  // 'Deal Me In' so no hands play while the player reads.
  if (table.status === 'waiting' && !gameState && !(table as any).challenge) {
    return (
      <Box minH="100vh" bg={POKER_COLORS.darkBg} p={4}>
        <VStack spacing={6} maxW="600px" mx="auto">
          {/* Header */}
          <HStack w="100%" justify="space-between">
            <VStack align="start" spacing={1}>
              <HStack>
                <Heading size="md" color={POKER_COLORS.cyan}>
                  {table.tableName}
                </Heading>
                <Badge colorScheme="purple">TRAINING</Badge>
              </HStack>
              <Text color="gray.400" fontSize="sm">
                ${table.smallBlind}/${table.bigBlind} blinds
              </Text>
            </VStack>
            <PokerButton variant="secondary" size="sm" onClick={() => navigate('/training')}>
              Back
            </PokerButton>
          </HStack>

          {/* Invite Code */}
          <PokerCard w="100%">
            <VStack spacing={3}>
              <Text color="gray.400" fontSize="sm">Invite Code</Text>
              <HStack>
                <Text color={POKER_COLORS.cyan} fontSize="2xl" fontFamily="mono" fontWeight="bold">
                  {table.inviteCode}
                </Text>
                <PokerButton size="sm" onClick={onCopy}>
                  {hasCopied ? <Check size={16} /> : <Copy size={16} />}
                </PokerButton>
              </HStack>
            </VStack>
          </PokerCard>

          {/* Players */}
          <PokerCard w="100%">
            <VStack align="start" spacing={3}>
              <HStack>
                <Users size={18} color={POKER_COLORS.cyan} />
                <Text color={POKER_COLORS.cyan} fontWeight="bold">
                  Players ({table.players.length})
                </Text>
              </HStack>
              {table.players.map((playerId, idx) => (
                <HStack key={playerId} w="100%" justify="space-between">
                  <Text color="white">
                    {table.playerNames?.[idx] || playerId.substring(0, 8)}
                  </Text>
                  {playerId === table.hostId && (
                    <Badge colorScheme="yellow">Host</Badge>
                  )}
                </HStack>
              ))}
            </VStack>
          </PokerCard>

          {/* Bots */}
          <PokerCard w="100%">
            <VStack align="start" spacing={3}>
              <HStack>
                <Bot size={18} color={POKER_COLORS.gold} />
                <Text color={POKER_COLORS.gold} fontWeight="bold">
                  Bots ({table.bots.length})
                </Text>
              </HStack>
              {table.bots.map(bot => (
                <HStack key={bot.uid} w="100%" justify="space-between">
                  <Text color="white">{bot.username}</Text>
                  <Badge
                    colorScheme={
                      bot.difficulty === 'easy' ? 'green' :
                      bot.difficulty === 'medium' ? 'yellow' : 'red'
                    }
                  >
                    {bot.difficulty.toUpperCase()}
                  </Badge>
                </HStack>
              ))}
            </VStack>
          </PokerCard>

          {/* Start Button (Host only) */}
          {isHost && (
            <PokerButton
              variant="primary"
              size="lg"
              w="100%"
              onClick={handleStartGame}
              isLoading={isStarting}
              isDisabled={table.players.length + table.bots.length < 2}
            >
               Start Training Game

            </PokerButton>
          )}

          {!isHost && (
            <Text color="gray.400" textAlign="center">
              Waiting for host to start the game...
            </Text>
          )}
        </VStack>
      </Box>
    );
  }

  // Game view
  return (
    <Box
      minH="100vh" bg={POKER_COLORS.darkBg} display="flex" flexDir="column"
      p={2}
      /* iOS: never let a wide row bleed past the screen edge, and keep the
         chrome clear of the notch and the home indicator. */
      overflowX="hidden"
      maxW="100vw"
      pt="max(8px, env(safe-area-inset-top))"
      pb="max(8px, env(safe-area-inset-bottom))"
    >
      {/* Header - wraps on a phone instead of pushing its ends off-screen */}
      <HStack
        justify="space-between" p={2} bg="blackAlpha.400" borderRadius="md" mb={2}
        flexWrap="wrap" rowGap={2} spacing={2}
      >
        <HStack minW={0} flexShrink={1}>
          <Text
            color={POKER_COLORS.cyan} fontWeight="bold"
            fontSize={{ base: 'sm', md: 'md' }}
            noOfLines={1} maxW={{ base: '46vw', md: 'none' }}
          >
            {table.tableName}
          </Text>
          <Badge colorScheme="purple" fontSize="xs">TRAINING</Badge>
        </HStack>
        <HStack spacing={2} flexWrap="wrap" rowGap={2} justify="flex-end">
          {/*  Sound Toggle Button */}

          <Box
            as="button"
            onClick={() => setSoundEnabled(!soundEnabled)}
            p={2}
            borderRadius="md"
            bg={soundEnabled ? 'green.600' : 'gray.600'}
            color="white"
            _hover={{ opacity: 0.8 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </Box>
          <Text color="gray.400" fontSize="sm">
            Hand #{gameState?.handNumber || 1}
          </Text>
          {/* ⏱ HAND TIMER - always-visible countdown when it's your turn */}
          {isMyTurn && !isPaused && (
            <Box
              px={3} py={1} borderRadius="full"
              bg={turnTimeLeft <= 5 ? 'red.600' : turnTimeLeft <= 10 ? 'orange.500' : 'green.600'}
              css={turnTimeLeft <= 5 ? { animation: 'pulse 0.6s ease-in-out infinite' } : undefined}
            >
              <Text color="white" fontWeight="bold" fontSize="sm" fontFamily="mono">
                0:{turnTimeLeft.toString().padStart(2, '0')}
              </Text>
            </Box>
          )}
          {/* ⏸ PAUSE - single-player perk (host controls it in co-op) */}
          {isHost && (
            <PokerButton
              variant={isPaused ? undefined : 'secondary'}
              size="sm"
              onClick={() => setIsPaused(p => !p)}
              bg={isPaused ? 'orange.500' : undefined}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </PokerButton>
          )}
          <PokerButton
            variant="secondary"
            size="sm"
            onClick={() => setShowLeaveModal(true)}
          >
            Leave
          </PokerButton>
        </HStack>
      </HStack>

      {/* 🏆 CHALLENGE GOAL HUD - mission line + live progress */}
      {(table as any)?.challenge && !challengeResult && (() => {
        const ch = (table as any).challenge;
        const goalType = ch.goalType || 'chips';
        const hero = gameState?.players?.find((p: any) => p.id === currentUserId);
        // Chips already bet this hand still count as yours until they're
        // lost - without this the HUD read '0 / 4,000' mid-jam.
        const heroChips = hero ? (hero.chips || 0) + ((hero as any).totalBetThisHand || 0) : ch.heroChips;
        const handsUsed = gameState?.handNumber || 1;
        const handsLeft = Math.max(0, ch.maxHands - handsUsed);
        // Tournament clock: current level + when blinds jump next
        const { sb: curSb, bb, ante: curAnte, level: curLevel } = getBlindsForHand(handsUsed);
        const handsToNextLevel = BLIND_LEVEL_HANDS - ((handsUsed - 1) % BLIND_LEVEL_HANDS);

        // Progress shown chips-first so beginners who don't know what a
        // "big blind" is still understand the goal at a glance.
        let pct: number;
        let progressLabel: string;
        if (goalType === 'preflopWins') {
          pct = Math.min(100, Math.round((preflopWins / (ch.goalCount || 1)) * 100));
          progressLabel = `${preflopWins} / ${ch.goalCount} pots won preflop`;
        } else if (goalType === 'survive') {
          pct = Math.min(100, Math.round((handsUsed / ch.maxHands) * 100));
          progressLabel = `${heroChips.toLocaleString()} chips (keep ${ch.goalChips.toLocaleString()}+)`;
        } else {
          pct = Math.min(100, Math.round((heroChips / ch.goalChips) * 100));
          progressLabel = `${heroChips.toLocaleString()} / ${ch.goalChips.toLocaleString()} chips`;
        }
        const onTrack = goalType === 'survive' ? heroChips >= ch.goalChips : pct >= 100;

        return (
          <Box p={3} mb={2} bg="rgba(245,158,11,0.10)" border="1px solid rgba(245,158,11,0.45)"
            borderRadius="md">
            <HStack justify="space-between" mb={1} flexWrap="wrap">
              <HStack>
                <Target size={15} color="#f59e0b" />
                <Text color="#f59e0b" fontSize="sm" fontWeight="bold">{ch.title}</Text>
                <Text color="gray.300" fontSize="xs" display={{ base: 'none', md: 'block' }}>
                  — {ch.mission}
                </Text>
              </HStack>
              {/* Wraps on a phone - as a rigid row this ran off both edges,
                  taking the drill title and the Glossary link with it. */}
              <HStack spacing={3} flexWrap="wrap" rowGap={1}>
                <Text color="#22d3ee" fontSize="xs" fontWeight="bold">
                  L{curLevel} · {curSb}/{bb}{curAnte ? ` (${curAnte})` : ''}
                </Text>
                <Text color="gray.400" fontSize="xs">
                  blinds up in {handsToNextLevel} hand{handsToNextLevel === 1 ? '' : 's'}
                </Text>
                <Text color={handsLeft <= 3 ? 'red.300' : 'gray.300'} fontSize="xs" fontWeight="bold">
                  {handsLeft} hands left
                </Text>
                <Text
                  color="gray.400" fontSize="xs" cursor="pointer" textDecoration="underline"
                  onClick={() => setBriefingDismissed(false)}
                >
                  Mission
                </Text>
                <Text
                  color="gray.400" fontSize="xs" cursor="pointer" textDecoration="underline"
                  onClick={() => window.open('/training/glossary?category=tournament', '_blank')}
                  title="Poker terms explained in plain English (opens in a new tab)"
                >
                  Glossary
                </Text>
              </HStack>
            </HStack>
            <HStack spacing={3}>
              <Box flex={1} bg="whiteAlpha.200" borderRadius="full" h="10px" overflow="hidden">
                <Box h="100%" w={`${Math.max(3, pct)}%`}
                  bg={onTrack ? '#4ade80' : '#f59e0b'}
                  transition="width 0.4s" borderRadius="full" />
              </Box>
              <Text color="white" fontSize="sm" fontWeight="bold" whiteSpace="nowrap">
                {progressLabel}
              </Text>
            </HStack>
          </Box>
        );
      })()}

      {/* 🏆 MISSION BRIEFING - what the challenge is and how to beat it.
          The game does NOT start until 'Deal Me In': challenge tables are
          created in 'waiting' status so no hands play behind this modal. */}
      <Modal
        isOpen={!!(table as any)?.challenge && !briefingDismissed && !challengeResult}
        onClose={() => {}}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
        size={{ base: 'full', md: 'xl' }}
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg={POKER_COLORS.darkTeal} border="2px solid" borderColor="#f59e0b"
          maxH={{ base: '100dvh', md: '90vh' }}>
          <ModalHeader color="#f59e0b">
            <HStack>
              <Target size={20} />
              <Text>{(table as any)?.challenge?.title}</Text>
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box bg="rgba(245,158,11,0.12)" border="1px solid rgba(245,158,11,0.4)"
                borderRadius="md" p={3}>
                <Text fontSize="xs" color="#f59e0b" fontWeight="bold" letterSpacing="wide" mb={1}>
                  YOUR MISSION
                </Text>
                <Text color="white" fontSize="md" fontWeight="600">
                  {(table as any)?.challenge?.mission}
                </Text>
                {/* Beginner-friendly translation: exact chip numbers, with
                    the big-blind jargon explained rather than assumed. */}
                {(() => {
                  const ch = (table as any)?.challenge;
                  if (!ch) return null;
                  const bbNow = table?.bigBlind || 1;
                  if (ch.goalType === 'preflopWins') {
                    return (
                      <Text color="gray.300" fontSize="sm" mt={2}>
                        In plain terms: raise before any shared cards are dealt and
                        make everyone fold - do that {ch.goalCount} times within {ch.maxHands} hands.
                      </Text>
                    );
                  }
                  if (ch.goalType === 'survive') {
                    return (
                      <Text color="gray.300" fontSize="sm" mt={2}>
                        In plain terms: you start with {ch.heroChips.toLocaleString()} chips.
                        Still have {ch.goalChips.toLocaleString()}+ chips after {ch.maxHands} hands
                        and you win. (One "big blind" = {bbNow.toLocaleString()} chips right now,
                        and it grows every few hands.)
                      </Text>
                    );
                  }
                  return (
                    <Text color="gray.300" fontSize="sm" mt={2}>
                      In plain terms: you start with {ch.heroChips.toLocaleString()} chips -
                      reach {ch.goalChips.toLocaleString()} chips within {ch.maxHands} hands.
                      (One "big blind" = {bbNow.toLocaleString()} chips right now, and it
                      grows every few hands.)
                    </Text>
                  );
                })()}
              </Box>

              {(table as any)?.challenge?.skill && (
                <Box bg="rgba(6,182,212,0.10)" border="1px solid rgba(6,182,212,0.35)"
                  borderRadius="md" p={3}>
                  <Text fontSize="xs" color={POKER_COLORS.cyan} fontWeight="bold" letterSpacing="wide" mb={1}>
                    TOURNAMENT SKILL YOU'RE TRAINING
                  </Text>
                  <Text color="gray.200" fontSize="sm" fontWeight="600">
                    {(table as any).challenge.skill}
                  </Text>
                </Box>
              )}

              <Box>
                <Text fontSize="xs" color={POKER_COLORS.cyan} fontWeight="bold" letterSpacing="wide" mb={2}>
                  HOW TO BEAT IT
                </Text>
                <VStack align="stretch" spacing={2}>
                  {((table as any)?.challenge?.tips || []).map((tip: string, i: number) => (
                    <HStack key={i} align="start" spacing={2}>
                      <Text color={POKER_COLORS.cyan} fontSize="sm" fontWeight="bold">{i + 1}.</Text>
                      <Text color="gray.300" fontSize="sm">{tip}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {/* 📖 KEY TERMS - plain-English definitions for the jargon this
                  challenge uses, right where a beginner needs them. */}
              {(() => {
                const concept = (table as any)?.challenge?.concept;
                const keys: string[] = (concept && CONCEPT_GLOSSARY_KEYS[concept]) || [];
                const entries = keys.map(k => POKER_GLOSSARY[k]).filter(Boolean);
                if (entries.length === 0) return null;
                return (
                  <Box bg="whiteAlpha.100" borderRadius="md" p={3}>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="xs" color="gray.400" fontWeight="bold" letterSpacing="wide">
                        KEY TERMS (NEW TO THESE?)
                      </Text>
                      <Text
                        fontSize="xs" color={POKER_COLORS.cyan} cursor="pointer"
                        textDecoration="underline"
                        onClick={() => window.open('/training/glossary?category=tournament', '_blank')}
                      >
                        Full Glossary ↗
                      </Text>
                    </HStack>
                    <VStack align="stretch" spacing={2}>
                      {entries.map(e => (
                        <Box key={e.term}>
                          <Text fontSize="sm" color="white" fontWeight="600" as="span">{e.term}: </Text>
                          <Text fontSize="sm" color="gray.300" as="span">
                            {e.simpleDefinition || e.definition}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                );
              })()}

              <HStack justify="space-between" bg="whiteAlpha.100" borderRadius="md" p={3}>
                <HStack>
                  <Coins size={16} color={POKER_COLORS.gold} />
                  <Text color="gray.300" fontSize="sm">Reward on completion</Text>
                </HStack>
                <Text color={POKER_COLORS.gold} fontWeight="bold">
                  ${((table as any)?.challenge?.rewardPkCash || 0).toFixed(2)} PSS Coins
                </Text>
              </HStack>

              {/* 👥 CO-OP: up to 2 friends can join before the deal and run
                  the same challenge at the same table - everyone chases the
                  goal with their own stack, everyone claims their own reward. */}
              {table?.status === 'waiting' && (
                <Box bg="whiteAlpha.100" borderRadius="md" p={3}>
                  <Text fontSize="xs" color="gray.400" fontWeight="bold" letterSpacing="wide">
                    PLAY WITH FRIENDS (OPTIONAL)
                  </Text>
                  <HStack justify="space-between" align="center" mt={2} spacing={4}>
                    <Text fontSize="xs" color="gray.400" flex={1}>
                      Up to 2 friends can join with this code (Training → Join by Code)
                      before you deal. Everyone competes for the same goal.
                    </Text>
                    <Box
                      as="button"
                      onClick={onCopy}
                      flexShrink={0}
                      px={4} py={2}
                      borderRadius="md"
                      bg="whiteAlpha.200"
                      border="1px solid"
                      borderColor={POKER_COLORS.cyan}
                      _hover={{ bg: 'whiteAlpha.300' }}
                      display="flex"
                      alignItems="center"
                      gap="8px"
                    >
                      <Text
                        color={POKER_COLORS.cyan}
                        fontWeight="bold"
                        fontSize="md"
                        fontFamily="mono"
                        letterSpacing="2px"
                        whiteSpace="nowrap"
                      >
                        {hasCopied ? 'COPIED!' : table?.inviteCode || ''}
                      </Text>
                      {hasCopied
                        ? <Check size={15} color={POKER_COLORS.cyan} />
                        : <Copy size={15} color={POKER_COLORS.cyan} />}
                    </Box>
                  </HStack>
                  {(table?.players?.length || 0) > 1 && (
                    <Text fontSize="xs" color="green.300" mt={2} fontWeight="bold">
                      {table.players.length} players seated
                    </Text>
                  )}

                  {/* 📲 One-tap friend invites: writes an invite the Cloud
                      Function turns into a phone push; tapping it auto-joins
                      this table via the code. */}
                  {!showInviteFriends ? (
                    <Text
                      fontSize="xs" color={POKER_COLORS.cyan} mt={2}
                      cursor="pointer" textDecoration="underline"
                      onClick={async () => {
                        setShowInviteFriends(true);
                        setLoadingFriends(true);
                        try {
                          setFriendsList(await getFriends(currentUserId));
                        } catch (err) {
                          console.error('[INVITE] Friends load failed:', err);
                        }
                        setLoadingFriends(false);
                      }}
                    >
                      Invite from your friends list (sends a phone notification)
                    </Text>
                  ) : (
                    <Box mt={2} maxH="150px" overflowY="auto" pr={1}>
                      {loadingFriends ? (
                        <Spinner size="sm" color={POKER_COLORS.cyan} />
                      ) : friendsList.length === 0 ? (
                        <Text fontSize="xs" color="gray.500">
                          No friends yet - friends are players who follow each
                          other. Share the code above instead.
                        </Text>
                      ) : (
                        <VStack align="stretch" spacing={1.5}>
                          {friendsList.map((f: any) => (
                            <HStack key={f.userId} justify="space-between">
                              <Text fontSize="sm" color="gray.200" noOfLines={1}>
                                {f.username || f.displayName || 'Player'}
                              </Text>
                              {invitedIds.has(f.userId) ? (
                                <HStack spacing={1}>
                                  <Check size={13} color="#4ade80" />
                                  <Text fontSize="xs" color="green.300" fontWeight="bold">Sent</Text>
                                </HStack>
                              ) : (
                                <PokerButton
                                  size="sm" variant="secondary"
                                  onClick={() => {
                                    sendTrainingInvite({
                                      targetUserId: f.userId,
                                      inviterId: currentUserId,
                                      inviterName: username,
                                      tableId: tableId || '',
                                      tableName: (table as any)?.challenge?.title || table?.tableName || 'Training Table',
                                      inviteCode: table?.inviteCode || '',
                                    });
                                    setInvitedIds(prev => new Set(prev).add(f.userId));
                                  }}
                                >
                                  Invite
                                </PokerButton>
                              )}
                            </HStack>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <PokerButton
              w="100%"
              isLoading={isStarting}
              isDisabled={!isHost && table?.status === 'waiting'}
              onClick={async () => {
                // Start dealing only now - the whole point of the briefing.
                if (table?.status === 'waiting' && isHost) {
                  await handleStartGame();
                }
                setBriefingDismissed(true);
              }}
            >
              {!isHost && table?.status === 'waiting'
                ? 'Waiting for the host to deal...'
                : 'Deal Me In'}
            </PokerButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ⏸ PAUSED BANNER */}
      {isPaused && (
        <Box p={2} mb={2} bg="rgba(249,115,22,0.15)" border="1px solid rgba(249,115,22,0.5)"
          borderRadius="md" textAlign="center">
          <Text color="orange.300" fontSize="sm" fontWeight="bold">
            GAME PAUSED - timer and bots are frozen. Hit Resume when you're ready.
          </Text>
        </Box>
      )}

      {/* 🆕 STUCK GAME WARNING - Shows when isStuck is true */}
      {isStuck && (
        <Box
          p={3}
          mb={2}
          bg="orange.500"
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <HStack>
            <RefreshCw size={18} />
            <Text color="white" fontWeight="bold">
              Game seems stuck?
            </Text>
          </HStack>
          <PokerButton
            size="sm"
            onClick={handleForceRecovery}
            isLoading={isRecovering}
            bg="white"
            color="orange.500"
            _hover={{ bg: 'gray.100' }}
          >
             Refresh Game

          </PokerButton>
        </Box>
      )}

      {/* Game Table */}
      {gameState ? (
        <VStack spacing={{ base: 2, md: 4 }} flex={1} w="100%" maxW="900px" mx="auto">
          {/* Poker Table SVG. Phones: the felt yields height to the page's
              own chrome (header, hole cards, action bar) so the decision
              buttons are on screen without scrolling - the default portrait
              height assumes a full-screen game page, which this is not. */}
          <Box w="100%" flex={1} position="relative"
            style={{ ['--pss-felt-h' as any]: 'max(300px, calc(100dvh - 400px))' }}>
            <PokerTableSVG
              players={playersForSVG}
              currentPlayerIndex={gameState.currentPlayerIndex}
              pot={trainingRunoutPhase === 'running' && runoutPotDisplay !== null ? runoutPotDisplay : gameState.pot}
              communityCards={trainingRunoutPhase === 'running'
                ? (gameState.communityCards || []).slice(0, runoutBoardCount)
                : (gameEngine.getCommunityCardsForRound ? gameEngine.getCommunityCardsForRound(gameState) : gameState.communityCards)}
              currentUserId={currentUserId}
              round={gameState.round}
              handResult={gameState.handResult}
              turnTimeLeft={turnTimeLeft}
              turnDuration={TRAINING_TURN_SECONDS}
              allInShowdown={Boolean((gameState as any).allInShowdown)}
              allInOccurred={Boolean((gameState as any).allInOccurred)}
              runoutPhase={trainingRunoutPhase}
              playerEquities={trainingEquities}
            />
          </Box>

          {/* Player's Hole Cards */}
          {currentPlayer?.holeCards && currentPlayer.holeCards.length > 0 && (
            <VStack spacing={1} mb={2}>
              <HStack spacing={2} justify="center">
                {currentPlayer.holeCards.map((card, idx) => (
                  <Box
                    key={idx}
                    bg="white"
                    borderRadius="8px"
                    w="60px"
                    h="84px"
                    display="flex"
                    flexDir="column"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="lg"
                    border="2px solid"
                    borderColor={card.suit === 'hearts' || card.suit === 'diamonds' ? 'red.500' : 'gray.800'}
                  >
                    <Text fontSize="xl" fontWeight="bold" color={card.suit === 'hearts' || card.suit === 'diamonds' ? 'red.500' : 'gray.800'}>
                      {card.rank}
                    </Text>
                    <Text fontSize="2xl" color={card.suit === 'hearts' || card.suit === 'diamonds' ? 'red.500' : 'gray.800'}>
                      {card.suit === 'hearts' ? '♥' : card.suit === 'diamonds' ? '♦' : card.suit === 'clubs' ? '♣' : '♠'}

                    </Text>
                  </Box>
                ))}
              </HStack>
              {/* Hand Strength Indicator */}
              {gameState && gameState.round !== 'preflop' && gameState.communityCards?.length > 0 && (() => {
                const hand = handEvaluationService.evaluateHand(
                  currentPlayer.holeCards,
                  gameState.communityCards
                );
                const description = hand ? handEvaluationService.getHandDescription(hand) : null;
                if (!description) return null;
                
                // Color based on hand strength
                const getHandColor = (desc: string) => {
                  if (desc.includes('Royal') || desc.includes('Straight Flush')) return 'purple.400';
                  if (desc.includes('Four') || desc.includes('Full House')) return 'yellow.400';
                  if (desc.includes('Flush') || desc.includes('Straight')) return 'green.400';
                  if (desc.includes('Three') || desc.includes('Two Pair')) return 'blue.400';
                  if (desc.includes('Pair')) return 'cyan.400';
                  return 'gray.400';
                };
                
                return (
                  <Badge
                    bg={getHandColor(description)}
                    color="white"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="bold"
                    textTransform="none"
                  >
                    {description}
                  </Badge>
                );
              })()}
            </VStack>
          )}

          {/* Raise Controls - Mobile Optimized */}
          {showRaiseSlider && currentPlayer && isMyTurn && (
            <VStack
              spacing={3}
              p={{ base: 3, md: 4 }}
              bg="blackAlpha.700"
              borderRadius="lg"
              w="100%"
              maxW="400px"
              mx="auto"
              css={{
                animation: 'slideUp 0.2s ease-out',
                '@keyframes slideUp': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <HStack w="100%" justify="space-between">
                <Text color="gray.300" fontSize="sm">Min: ${minRaise}</Text>
                <Text color="gray.300" fontSize="sm">Max: ${currentPlayer.chips.toFixed(2)}</Text>
              </HStack>
              
              {/* Touch-friendly Slider */}
              <Box w="100%" px={2} py={3}>
                <Slider
                  min={minRaise}
                  max={currentPlayer.chips}
                  value={raiseAmount || minRaise}
                  onChange={(val) => { triggerHaptic('light'); setRaiseAmount(val); }}
                  focusThumbOnChange={false}
                >
                  <SliderTrack bg="gray.600" h="8px" borderRadius="full">
                    <SliderFilledTrack bg="yellow.500" />
                  </SliderTrack>
                  <SliderThumb 
                    boxSize="32px" 
                    bg="yellow.400"
                    border="3px solid"
                    borderColor="yellow.600"
                    _focus={{ boxShadow: '0 0 0 3px rgba(236, 201, 75, 0.4)' }}
                    _active={{ transform: 'scale(1.2)' }}
                  />
                </Slider>
              </Box>
              
              {/* Quick Bet Buttons */}
              <HStack spacing={2} w="100%" justify="center" flexWrap="wrap">
                <PokerButton
                  size="sm"
                  minH={TOUCH_TARGETS.minimum}
                  px={3}
                  onClick={() => { triggerHaptic('light'); setRaiseAmount(potFractionRaise(1 / 3)); }}
                >
                  ⅓ Pot
                </PokerButton>
                <PokerButton
                  size="sm"
                  minH={TOUCH_TARGETS.minimum}
                  px={3}
                  onClick={() => { triggerHaptic('light'); setRaiseAmount(potFractionRaise(0.5)); }}
                >
                  ½ Pot
                </PokerButton>
                <PokerButton
                  size="sm"
                  minH={TOUCH_TARGETS.minimum}
                  px={3}
                  onClick={() => { triggerHaptic('light'); setRaiseAmount(potFractionRaise(1)); }}
                >
                  Pot
                </PokerButton>
                <PokerButton
                  size="sm"
                  minH={TOUCH_TARGETS.minimum}
                  px={3}
                  onClick={() => { triggerHaptic('medium'); setRaiseAmount(currentPlayer.chips); }}
                  bg="orange.500"
                  _hover={{ bg: 'orange.400' }}
                >
                  ALL-IN
                </PokerButton>
              </HStack>
              
              {/* Editable amount input */}
              <HStack w="100%" justify="center">
                <Text color="white" fontSize="lg">$</Text>
                <Input
                  type="number"
                  value={raiseAmount || ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : Number(e.target.value);
                    setRaiseAmount(val);
                  }}
                  onBlur={() => {
                    // Clamp value on blur
                    if (raiseAmount < minRaise) setRaiseAmount(minRaise);
                    if (raiseAmount > currentPlayer.chips) setRaiseAmount(currentPlayer.chips);
                  }}
                  bg="blackAlpha.600"
                  color="white"
                  fontWeight="bold"
                  fontSize="xl"
                  textAlign="center"
                  w="120px"
                  h={TOUCH_TARGETS.comfortable}
                  border="2px solid"
                  borderColor={raiseAmount < minRaise || raiseAmount > currentPlayer.chips ? 'red.500' : 'yellow.500'}
                  _focus={{ borderColor: 'yellow.400', boxShadow: '0 0 0 1px var(--chakra-colors-yellow-400)' }}
                />
              </HStack>
              
              {/* Error messages - a full-stack shove is exempt from min-raise */}
              {raiseAmount < minRaise && raiseAmount < currentPlayer.chips && (
                <Text color="red.400" fontSize="sm">
                  Minimum raise is ${minRaise}{currentPlayer.chips < minRaise ? ` - or go ALL-IN for $${currentPlayer.chips.toFixed(2)}` : ''}
                </Text>
              )}
              {raiseAmount > currentPlayer.chips && (
                <Text color="red.400" fontSize="sm">
                   Maximum is ${currentPlayer.chips.toFixed(2)} (your stack)

                </Text>
              )}
              
              {/* Confirm/Cancel Buttons - shoving your whole stack is ALWAYS
                  legal even below the min-raise (all-in for less). The old
                  validation blocked exactly that and got players timer-folded
                  while fighting the slider. */}
              <HStack spacing={3} w="100%">
                <PokerButton
                  flex={1}
                  onClick={() => {
                    triggerHaptic('success');
                    if (raiseAmount >= currentPlayer.chips) {
                      handleAction('all-in');
                    } else {
                      handleAction('raise', raiseAmount);
                    }
                  }}
                  isDisabled={
                    isProcessingAction
                    || raiseAmount > currentPlayer.chips
                    || (raiseAmount < minRaise && raiseAmount < currentPlayer.chips)
                  }
                  bg={raiseAmount >= currentPlayer.chips ? 'orange.500' : 'green.500'}
                  _hover={{ bg: raiseAmount >= currentPlayer.chips ? 'orange.400' : 'green.400' }}
                  _active={{ transform: 'scale(0.97)' }}
                  minH={TOUCH_TARGETS.comfortable}
                  fontWeight="bold"
                >
                  {raiseAmount >= currentPlayer.chips
                    ? `ALL-IN $${currentPlayer.chips.toFixed(2)}`
                    : `RAISE TO $${(maxBet + raiseAmount).toFixed(2)}`}
                </PokerButton>
                <PokerButton
                  variant="secondary"
                  onClick={() => { triggerHaptic('light'); setShowRaiseSlider(false); }}
                  minH={TOUCH_TARGETS.comfortable}
                >
                  Cancel
                </PokerButton>
              </HStack>
            </VStack>
          )}

          {/* Action Buttons - Mobile Optimized. Phones: pinned to the bottom
              of the viewport so a decision is always one tap away even if
              the page scrolls. */}
          {isMyTurn && currentPlayer && (
            <HStack
              spacing={{ base: 2, md: 3 }}
              justify="center"
              flexWrap="wrap"
              p={{ base: 2, md: 3 }}
              bg={{ base: 'rgba(8, 12, 20, 0.95)', md: 'blackAlpha.600' }}
              borderRadius="lg"
              w="100%"
              position={{ base: 'sticky', md: 'static' }}
              bottom={{ base: '2px', md: 'auto' }}
              zIndex={40}
              css={{
                paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
              }}
            >
              {/* Call / Check */}
              {canCheck ? (
                <PokerButton
                  onClick={() => { triggerHaptic('medium'); handleAction('check'); }}
                  isDisabled={isProcessingAction}
                  bg="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                  _hover={{ bg: 'linear-gradient(135deg, #34d670 0%, #1cb254 100%)' }}
                  _active={{ transform: 'scale(0.97)' }}
                  size="lg"
                  flex={1}
                  minW={0}
                  px={1}
                  minH={TOUCH_TARGETS.large}
                  fontSize={{ base: 'sm', md: 'lg' }}
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  CHECK
                </PokerButton>
              ) : (
                <PokerButton
                  onClick={() => { triggerHaptic('medium'); handleAction('call'); }}
                  isDisabled={isProcessingAction}
                  bg="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                  _hover={{ bg: 'linear-gradient(135deg, #4f8ff8 0%, #2e6df0 100%)' }}
                  _active={{ transform: 'scale(0.97)' }}
                  size="lg"
                  flex={1}
                  minW={0}
                  px={1}
                  minH={TOUCH_TARGETS.large}
                  fontSize={{ base: 'sm', md: 'lg' }}
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  CALL ${callAmount % 1 === 0 ? callAmount.toFixed(0) : callAmount.toFixed(2)}
                </PokerButton>
              )}

              {/* Raise */}
              {canRaise && (
                <PokerButton
                  onClick={() => {
                    triggerHaptic('light');
                    setRaiseAmount(minRaise);
                    setShowRaiseSlider(!showRaiseSlider);
                  }}
                  isDisabled={isProcessingAction}
                  bg="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                  color="white"
                  _hover={{ bg: 'linear-gradient(135deg, #f7ab26 0%, #e28309 100%)' }}
                  _active={{ transform: 'scale(0.97)' }}
                  size="lg"
                  flex={1}
                  minW={0}
                  px={1}
                  minH={TOUCH_TARGETS.large}
                  fontSize={{ base: 'sm', md: 'lg' }}
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  RAISE
                </PokerButton>
              )}

              {/* All-in - one tap, always legal, same as the live tables */}
              <PokerButton
                onClick={() => { triggerHaptic('heavy'); handleAction('all-in'); }}
                isDisabled={isProcessingAction}
                bg="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                _hover={{ bg: 'linear-gradient(135deg, #f25b5b 0%, #e33636 100%)' }}
                _active={{ transform: 'scale(0.97)' }}
                size="lg"
                flex={1}
                minW={0}
                px={1}
                minH={TOUCH_TARGETS.large}
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="bold"
                textTransform="uppercase"
              >
                ALL-IN ${currentPlayer.chips >= 1000 ? `${Math.round(currentPlayer.chips / 100) / 10}k` : currentPlayer.chips.toFixed(0)}
              </PokerButton>

              {/* Fold */}
              <PokerButton
                onClick={() => {
                  triggerHaptic('heavy');
                  // Folding when checking is free deserves a second look -
                  // the classic beginner leak. Confirm via toast-level
                  // friction: one extra tap.
                  if (canCheck && !confirmFreeFoldRef.current) {
                    confirmFreeFoldRef.current = true;
                    toast({
                      title: 'Checking is free here',
                      description: 'No bet to call - tap FOLD again to fold anyway, or CHECK to see the next card for free.',
                      status: 'warning',
                      duration: 4000,
                    });
                    setTimeout(() => { confirmFreeFoldRef.current = false; }, 4000);
                    return;
                  }
                  confirmFreeFoldRef.current = false;
                  handleAction('fold');
                }}
                isDisabled={isProcessingAction}
                bg="linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
                _hover={{ bg: 'linear-gradient(135deg, #7c8695 0%, #5a6472 100%)' }}
                _active={{ transform: 'scale(0.97)' }}
                size="lg"
                flex={1}
                minW={0}
                px={1}
                minH={TOUCH_TARGETS.large}
                fontSize={{ base: 'sm', md: 'lg' }}
                fontWeight="bold"
                textTransform="uppercase"
              >
                FOLD
              </PokerButton>
            </HStack>
          )}

          {/* Waiting message */}
          {!isMyTurn && currentPlayer && !currentPlayer.folded && !currentPlayer.eliminated && gameState.status === 'playing' && gameState.round !== 'ended' && gameState.round !== 'showdown' && (
            <Box p={3} bg="blackAlpha.600" borderRadius="lg" textAlign="center">
              <Text color="gray.400">
                Waiting for {usernameMap[gameState.players[gameState.currentPlayerIndex]?.id] || 'opponent'}...
              </Text>
            </Box>
          )}

          {/* Winner announcement - pot won AND net profit. The pot includes
              the winner's own bets, so 'wins $2300' on a jam that only
              picked up the blinds read like chips had vanished. */}
          {(gameState.round === 'ended' || gameState.round === 'showdown') && gameState.winners && gameState.winners.length > 0 && (() => {
            const w0 = gameState.winners[0];
            const winnerSeat = gameState.players.find(p => p.id === w0.playerId);
            const ownContribution = winnerSeat?.totalBetThisHand || 0;
            const net = Math.max(0, w0.amount - ownContribution);
            return (
              <Box p={4} bg="yellow.500" borderRadius="lg" textAlign="center">
                <Text color="black" fontSize="lg" fontWeight="bold">
                  {usernameMap[w0.playerId] || 'Winner'} wins the ${w0.amount.toFixed(2)} pot
                </Text>
                <Text color="black" fontSize="md" fontWeight="bold">
                  +${net.toFixed(2)} profit{ownContribution > 0 ? ` (${ownContribution.toFixed(2)} of the pot was their own bet)` : ''}
                </Text>
                {w0.hand && (
                  <Text color="black" fontSize="sm">
                    {w0.hand}
                  </Text>
                )}
                {/* Challenge tables: answer 'am I done?' right here. */}
                {(() => {
                  const chq: any = (table as any)?.challenge;
                  if (!chq || w0.playerId !== currentUserId) return null;
                  if ((chq.goalType || 'chips') !== 'chips' && chq.goalType !== 'survive') return null;
                  const myChips = winnerSeat?.chips || 0;
                  return myChips >= chq.goalChips ? (
                    <Text color="black" fontSize="sm" fontWeight="bold" mt={1}>
                      GOAL REACHED: {myChips.toLocaleString()} / {Number(chq.goalChips).toLocaleString()} chips
                    </Text>
                  ) : (
                    <Text color="blackAlpha.800" fontSize="sm" fontWeight="bold" mt={1}>
                      Challenge: {myChips.toLocaleString()} / {Number(chq.goalChips).toLocaleString()} chips - {(chq.goalChips - myChips).toLocaleString()} to go
                    </Text>
                  );
                })()}
                <Text color="blackAlpha.700" fontSize="sm" mt={2}>
                  Next hand starting soon...
                </Text>
              </Box>
            );
          })()}
        </VStack>
      ) : (
        <Box p={8} textAlign="center">
          <Spinner color={POKER_COLORS.cyan} size="xl" />
          <Text color="gray.400" mt={4}>
            {(table as any)?.challenge && table.status === 'waiting'
              ? 'Table ready - read your mission briefing'
              : 'Loading game state...'}
          </Text>
        </Box>
      )}

      {/* Leave Modal */}
      <Modal isOpen={showLeaveModal} onClose={() => setShowLeaveModal(false)} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg={POKER_COLORS.darkTeal} border="1px solid" borderColor={POKER_COLORS.cyan}>
          <ModalHeader color={POKER_COLORS.cyan}>Leave Training?</ModalHeader>
          <ModalBody>
            <Text color="gray.300">
              {gameState && gameState.handNumber > 1
                ? `You've played ${gameState.handNumber - 1} hands. A training report will be generated when you leave.`
                : 'Are you sure you want to leave the training table?'}
            </Text>
          </ModalBody>
          {/* Three buttons in one row do not fit a phone - the third ran off
              the screen entirely. Stack them full-width on narrow screens. */}
          <ModalFooter
            gap={3}
            flexDirection={{ base: 'column', sm: 'row' }}
            sx={{ '& > button': { width: ['100%', 'auto'] } }}
          >
            <PokerButton variant="secondary" onClick={() => setShowLeaveModal(false)}>
              Keep Playing
            </PokerButton>
            <PokerButton variant="secondary" isLoading={isLeaving} onClick={() => handleLeaveTable(false)}>
              Just Leave
            </PokerButton>
            <PokerButton variant="primary" isLoading={isLeaving} onClick={() => handleLeaveTable(true)}>
              Leave & View Report
            </PokerButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 🏆 CHALLENGE RESULT MODAL - mini-game win/lose + reward claim */}
      <Modal isOpen={!!challengeResult} onClose={() => {}} isCentered closeOnOverlayClick={false}
        size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg={POKER_COLORS.darkTeal} border="2px solid"
          maxH={{ base: '100dvh', md: '90vh' }}
          borderColor={challengeResult?.outcome === 'won' ? '#4ade80' : '#f87171'}>
          <ModalHeader color={challengeResult?.outcome === 'won' ? 'green.300' : 'red.300'}>
            <HStack>
              <Trophy size={20} />
              <Text>
                {challengeResult?.outcome === 'won' ? 'Challenge Complete!' : 'Challenge Failed'}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text color="white" fontWeight="bold">{(table as any)?.challenge?.title}</Text>
              <Text color="gray.300" fontSize="sm">
                {challengeResult?.outcome === 'won'
                  ? `You reached ${challengeResult.heroChips.toLocaleString()} chips in ${challengeResult.handsPlayed} hands - goal beaten!`
                  : challengeResult?.heroChips === 0
                    ? `Busted on hand ${challengeResult.handsPlayed}. Study the key lessons and run it back.`
                    : `Out of hands: ${challengeResult?.heroChips.toLocaleString()} chips after ${challengeResult?.handsPlayed} hands (goal ${((table as any)?.challenge?.goalChips || 0).toLocaleString()}).`}
              </Text>
              {challengeResult?.outcome === 'won' && !challengeClaimMsg && (
                <PokerButton
                  isLoading={challengeClaiming}
                  onClick={async () => {
                    const ch = (table as any)?.challenge;
                    if (!ch) return;
                    setChallengeClaiming(true);
                    // Same geo pattern as every training claim: PSS Coins in
                    // legal states, PlayCoins fallback in restricted ones.
                    const stateCode = geoService.getCachedResult()?.region;
                    const r = await gameApiService.claimTournamentChallenge(ch.id, [], stateCode);
                    setChallengeClaiming(false);
                    setChallengeClaimMsg(r.success
                      ? (r.alreadyClaimed
                        ? 'Already claimed - rewards pay once per challenge.'
                        : r.geoRestricted
                          ? `+${(r.playCoins ?? Math.round((ch.rewardPkCash || 0) * 100)).toLocaleString()} PlayCoins credited (PSS Coins unavailable in your state).`
                          : `+$${(r.rewardPkCash ?? ch.rewardPkCash).toFixed(2)} PSS Coins credited (1x playthrough).`)
                      : (r.error || 'Claim failed'));
                  }}
                >
                  <Coins size={14} style={{ marginRight: 6 }} />
                  Claim ${((table as any)?.challenge?.rewardPkCash || 0).toFixed(2)} PSS Coins
                </PokerButton>
              )}
              {challengeClaimMsg && (
                <Text fontSize="sm" color="green.300">{challengeClaimMsg}</Text>
              )}

              {/* 👥 CO-OP STANDINGS - who's beaten the goal, who's still
                  grinding. Each player claims their OWN reward when their
                  own run succeeds; results here are per-player. */}
              {(table?.players?.length || 0) > 1 && (() => {
                const ch = (table as any)?.challenge;
                if (!ch || !gameState) return null;
                const rows = (table?.players || []).map((uid: string) => {
                  const seat = gameState.players.find(p => p.id === uid);
                  const chips = seat?.chips || 0;
                  const met = ch.goalType === 'preflopWins'
                    ? (uid === currentUserId ? preflopWins >= (ch.goalCount || 1) : undefined)
                    : chips >= ch.goalChips;
                  return { uid, name: table?.playerNames?.[uid] || uid.substring(0, 8), chips, met };
                }).sort((a, b) => b.chips - a.chips);
                return (
                  <Box bg="whiteAlpha.100" borderRadius="md" p={3}>
                    <Text fontSize="xs" color="gray.400" fontWeight="bold" letterSpacing="wide" mb={2}>
                      TABLE STANDINGS
                    </Text>
                    <VStack align="stretch" spacing={1}>
                      {rows.map(r => (
                        <HStack key={r.uid} justify="space-between">
                          <Text fontSize="sm" color={r.uid === currentUserId ? 'cyan.300' : 'gray.200'}
                            fontWeight={r.uid === currentUserId ? 'bold' : 'normal'}>
                            {r.name}{r.uid === currentUserId ? ' (you)' : ''}
                          </Text>
                          <HStack>
                            <Text fontSize="sm" color="gray.300">{r.chips.toLocaleString()}</Text>
                            {r.met === true && <Text fontSize="xs" color="green.300" fontWeight="bold">GOAL ✓</Text>}
                            {r.met === false && <Text fontSize="xs" color="gray.500">grinding</Text>}
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                    <Text fontSize="10px" color="gray.500" mt={2}>
                      Rewards are individual - each player who completes the mission claims their own PSS Coins.
                    </Text>
                  </Box>
                );
              })()}
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            {(table?.players?.length || 0) > 1 && (
              <PokerButton
                variant="secondary"
                onClick={() => setChallengeResult(null)}
                title="Your result is locked in - keep playing hands while your friends finish their run"
              >
                Keep Playing
              </PokerButton>
            )}
            <PokerButton variant="secondary" isLoading={isLeaving} onClick={() => handleLeaveTable(true)}>
              View Training Report
            </PokerButton>
            <PokerButton onClick={() => navigate('/training/tournament')}>
              Back to Challenges
            </PokerButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TrainingGamePage;